<template>
  <div class="ppm-page" :class="{ 'ppm-page--fullscreen': isFullscreen }">

    <PpmHeader
      :project-guid="projectGuid"
      :project-name="selectedProjectName"
      :period-label="periodLabel"
      :period-range="periodRange"
      :filter-contractor="filterContractor"
      :filter-type="filterType"
      :contractor-options="contractorOptions"
      :project-type-options="projectTypeOptions"
      :is-loading="isLoading"
      :all-expanded="allExpanded"
      :expand-all-progress="expandAllProgress"
      :can-send="canSend"
      :usp-sending="uspSending"
      :show-full-names="showFullNames"
      :is-fullscreen="isFullscreen"
      :has-tree-data="!!treeData.length"
      :has-dates="!!calendarDates.length"
      :date-not-future="dateNotFuture"
      :is-future-date="isFutureDate"
      :storno-mode="stornoMode"
      :can-storno="hasStornoGrant"
      :can-storno-list="hasStornoListGrant"
      @update:storno-mode="stornoMode = $event"
      @open-project-dialog="projectDialog.show = true"
      @clear-project="clearProject"
      @clear-dates="clearDates"
      @collapse-all="collapseAll"
      @refresh-nodes="refreshNodes"
      @send-to-usp="sendToUsp"
      @open-json="openJson"
      @toggle-show-full-names="toggleShowFullNames"
      @open-usp-report="openUspReport"
      @open-discrepancy="openDiscrepancyDialog"
      @open-storno-list="openStornoListDialog"
      @toggle-fullscreen="toggleFullscreen"
      @update:period-range="periodRange = $event"
      @update:filter-contractor="filterContractor = $event"
      @update:filter-type="filterType = $event"
    />

    <!-- Индикатор фоновой проверки -->
    <transition name="fade">
      <div v-if="isCheckingWarnings" class="ppm-warn-check">
        <q-spinner-dots size="14px" color="orange" />
        <span>Проверка привязок…</span>
      </div>
    </transition>

    <!-- Пустое состояние -->
    <div v-if="!projectGuid && !isLoading" class="ppm-empty">
      <q-icon name="account_tree" color="grey-4" size="56px" />
      <p>Выберите проект УСП и период для автозагрузки</p>
    </div>
    <div v-else-if="projectGuid && !isLoading && !treeData.length" class="ppm-empty">
      <q-icon name="folder_off" color="grey-4" size="56px" />
      <p>Нет данных по проекту</p>
    </div>

    <PpmTable
      v-if="visibleRows.length > 0"
      ref="ppmTable"
      :visible-rows="visibleRows"
      :calendar-dates="calendarDates"
      :month-groups="monthGroups"
      :col-widths="colWidths"
      :expanded-set="expandedSet"
      :today-iso="todayIso"
      :is-future-date="isFutureDate"
      :show-full-names="showFullNames"
      :hierarchy-pct-map="hierarchyPctMap"
      :group-completion-map="groupCompletionMap"
      :parent-cell-data-map="parentCellDataMap"
      :floor-loading-ids="floorLoadingIds"
      :block-loading-ids="blockLoadingIds"
      :gpr-loading-guids="gprLoadingGuids"
      :completed-works="completedWorks"
      :cell-values="cellValues"
      :cell-meta="cellMeta"
      :cumulative-fact-sums="cumulativeFactSums"
      :work-fact-pct-map="workFactPctMap"
      :gpr-periods-by-name="gprPeriodsByName"
      :smeta-change-log="smetaChangeLog"
      :storno-mode="stornoMode"
      @toggle-expand="toggleExpand"
      @delete-fact="deleteFactByCell"
      @open-fact-dialog="openFactDialog"
      @open-group-fact-dialog="openGroupFactDialog"
      @open-fact-history="openFactHistoryDialog"
      @complete-work="requestCompleteWork"
      @uncomplete-work="requestUncompleteWork"
      @start-col-resize="startColResize"
    />
    <!-- Диалог выбора проекта -->
    <PpmProjectDialog
      v-model="projectDialog.show"
      :projects="projectGroups"
      :is-loading="isLoadingProjects"
      :is-ingesting="isIngestingProjects"
      @select="selectProject"
      @ingest="onIngestProjects"
    />

    <PpmJsonDialog
      v-model="jsonDialog.show"
      :content="jsonDialog.content"
      :action-type="jsonDialog.actionType"
      :sending="jsonDialog.sending"
      :unsent-count="jsonDialog.unsentFactIds ? jsonDialog.unsentFactIds.length : 0"
      @send="sendJsonAction"
    />

    <PpmFactDialog
      v-model="factDialog.show"
      :fact-data="factDialog"
      :fact-dialog-kzt-total="factDialogKztTotal"
      :storno-mode="stornoMode"
      :exceed-allowed="isFactExceedAllowed"
      @save="submitFactDialog"
      @save-storno="submitStornoDialog"
      @delete="deleteFactRow"
      @complete-work="requestCompleteWork"
      @uncomplete-work="requestUncompleteWork"
    />
    <PpmUspDialog
      v-model="uspDialog.show"
      :data="uspDialog"
      @build-json="doBuildUspJson"
      @copy-json="copyUspJson"
    />

    <!-- ═══════════════════════════════════════════
         Диалог: Подтверждение завершения работы
         ═══════════════════════════════════════════ -->
    <PpmCompleteDialog
      v-model="completeDialog.show"
      :loading="completeDialog.loading"
      @confirm="doCompleteWork"
    />
    <PpmAlignToSmetaDialog
      v-model="alignDialog.show"
      :subtitle="alignDialog.title"
      :summary="alignDialog.summary"
      :busy="alignDialog.busy"
      :applying="alignDialog.applying"
      :error="alignDialog.error"
      @apply="applyAlignToSmeta"
    />
    <PpmGroupFactDialog
      v-model="groupFactDialog.show"
      :group-data="groupFactDialog"
      :group-cumulative-pct="groupCumulativePct"
      :group-fact-overall-pct="groupFactOverallPct"
      :group-lock-icon-pct="groupLockIconPct"
      :group-fact-dialog-has-savable-works="groupFactDialogHasSavableWorks"
      :storno-mode="stornoMode"
      :exceed-allowed="isFactExceedAllowed"
      @save="submitGroupFactDialog"
      @save-storno="submitGroupStornoDialog"
    />
    <PpmDiscrepancyDialog
      ref="discrepancyDialogRef"
      v-model="discrepancyDialog.show"
      :data="discrepancyDialog"
      :can-action="hasUspActionGrant"
      :project-name="selectedProjectName"
      @open-detail="openDiscrepancyDetail"
      @refresh="refreshDiscrepancyData"
      @align-to-smeta="alignFactToSmeta"
      @align-to-smeta-bulk="alignFactToSmetaBulk"
      @align-to-usp-bulk="alignToUspBulk"
      @storno-bulk="stornoBulk"
      @send-smeta-bulk="sendSmetaBulk"
      @cron-update-smeta="runCronUpdateSmeta"
      @cron-create-contracts="runCronCreateContracts"
      @cron-history="openCronHistory"
      @cron-log-json="openCronLogJson"
    />

    <PpmStornoDialog
      v-model="stornoListDialog.show"
      :data="stornoListDialog"
      :project-name="selectedProjectName"
      :preset-filter-type="stornoListDialog.presetFilterType"
      @refresh="reloadStornoList"
      @storno-selected="stornoFromList"
    />

    <PpmDiscrepancyDetailDialog
      v-model="discrepancyDetailDialog.show"
      :data="discrepancyDetailDialog"
      :project-name="selectedProjectName"
    />
    <PpmUspReportDialog
      v-model="uspReportDialog.show"
      :data="uspReportDialog"
      @open-work-detail="openUspWorkDetail"
      @open-guid-detail="openUspGuidDetail"
      @refresh="refreshUspSbsData"
    />

    <PpmUspWorkDetailDialog
      v-model="uspWorkDetailDialog.show"
      :data="uspWorkDetailDialog"
    />

    <PpmUspGuidDetailDialog
      v-model="uspGuidDetailDialog.show"
      :data="uspGuidDetailDialog"
    />
    <PpmFactHistoryDialog
      v-model="factHistoryDialog.show"
      :data="factHistoryDialog"
      :storno-mode="stornoMode"
      :exceed-allowed="isFactExceedAllowed"
      @save-history-storno="submitHistoryStornoRow"
      @load-history-materials="loadHistoryMaterials"
      @history-material-input="onHistoryMaterialInput"
    />

    <PpmSmetaChangeDialog
      v-model="smetaChangeDialog.show"
      :data="smetaChangeDialog"
    />

  </div>
</template>

<script>
import {
  getProjectByGuid,
  getProjectGroups,
  ingestProjectGroups,
  getGprPeriods,
  getWorkSetsForFloor,
  getWorkSetsForMatrix,
  getWorkGuidByProject,
  checkWorkGuidsBatch,
  getWorkDetail,
  getUspFactMaterialByFact,
  uspFactSave,
  uspFactRead,
  uspFactSummaryRead,
  uspFactHistoryRead,
  getContractors,
  getContractorsSMR,
  getProjectTypes,
  projectPatternReadFiltered,
  getProjectTypesByContractor,
} from "@/views/ProjectPatternStructure/services";
import { uspWorkComplete, uspFactSend, uspFactBuildJsonSelected, uspFactBuildCorrectionJson, uspFactBuildStornoJson, uspFactBuildStornoJsonPartial, uspFactSendAction, uspFactSendActionPartial, uspFactSendSelected, uspFactDelete, uspFactCorrectionSave, uspFactCorrectionRead, uspFactFillWorkGuid, uspSbsDataRefresh, uspReportRead, uspReportWorkDetailRead, uspReportUspDetailRead, uspDiscrepancyRead, uspDiscrepancyDetailRead, uspStornoListRead, uspFactAlignToSmeta, smetaChangeLogUnsignedRead, cronLogJsonRead, cronHistoryRead, cronStatusRead, cronUpdateSmeta, cronCreateContracts, uspEstimateSendSelected, uspEstimateBuildJson } from "@/views/ProjectPatternStructure/services";
import { getHasGrant } from "@/views/ProjectsEntry/services";
import { useFormatters } from "./composables/useFormatters";
import {
  buildMaterialsJsonTriplet,
  hasMaterialsDiff,
  createStornoFields,
  buildOriginalMaterialsFromWork,
  applyMaterialInput,
} from "./composables/useStornoEditing";
import { STORNO_GRANT, STORNO_LIST_GRANT, ACTION_TYPE, ACTION_LABEL, STORNO_MESSAGES } from "./composables/stornoConstants";
import "./styles/storno.css";
import PpmJsonDialog from "./components/PpmJsonDialog.vue";
import PpmCompleteDialog from "./components/PpmCompleteDialog.vue";
import PpmAlignToSmetaDialog from "./components/PpmAlignToSmetaDialog.vue";
import PpmProjectDialog from "./components/PpmProjectDialog.vue";
import PpmDiscrepancyDetailDialog from "./components/PpmDiscrepancyDetailDialog.vue";
import PpmUspWorkDetailDialog from "./components/PpmUspWorkDetailDialog.vue";
import PpmUspGuidDetailDialog from "./components/PpmUspGuidDetailDialog.vue";
import PpmFactDialog from "./components/PpmFactDialog.vue";
import PpmGroupFactDialog from "./components/PpmGroupFactDialog.vue";
import PpmUspDialog from "./components/PpmUspDialog.vue";
import PpmDiscrepancyDialog from "./components/PpmDiscrepancyDialog.vue";
import PpmStornoDialog from "./components/PpmStornoDialog.vue";
import PpmUspReportDialog from "./components/PpmUspReportDialog.vue";
import PpmFactHistoryDialog from "./components/PpmFactHistoryDialog.vue";
import PpmHeader from "./components/PpmHeader.vue";
import PpmTable from "./components/PpmTable.vue";
import PpmSmetaChangeDialog from "./components/PpmSmetaChangeDialog.vue";

/**
 * Из плоского списка сохранённых строк строит группы по usp_work_guid.
 * Суммирует fact_value по всем этажам для каждой (work_guid, value_date) пары,
 * собирает сводку: totalValue, dateCount, floorCount, is_sent.
 */
function buildUspWorkGroups(rows) {
  const map = {};
  rows.forEach((r) => {
    const g = r.usp_work_guid;
    if (!g) return;
    if (!map[g]) {
      map[g] = {
        usp_work_guid: g,
        work_name:     r.work_name,
        is_sent:       true,
        totalValue:    0,
        dateCount:     0,
        floorCount:    0,
        dates:         {},
      };
    }
    const grp = map[g];
    if (!r.is_sent) grp.is_sent = false;
    grp.floorCount++;
    // Суммируем по датам
    const dk = r.value_date || 'null';
    if (!grp.dates[dk]) {
      grp.dates[dk] = 0;
      grp.dateCount++;
    }
    grp.dates[dk]    += r.fact_value || 0;
    grp.totalValue   += r.fact_value || 0;
  });
  return Object.values(map);
}

export default {
  name: "ProjectPatternPeriodMatrix",
  components: { PpmJsonDialog, PpmCompleteDialog, PpmAlignToSmetaDialog, PpmProjectDialog, PpmDiscrepancyDetailDialog, PpmUspWorkDetailDialog, PpmUspGuidDetailDialog, PpmFactDialog, PpmGroupFactDialog, PpmUspDialog, PpmDiscrepancyDialog, PpmStornoDialog, PpmUspReportDialog, PpmFactHistoryDialog, PpmHeader, PpmTable, PpmSmetaChangeDialog },
  setup() {
    const fmt = useFormatters();
    return { ...fmt };
  },
  data() {
    return {
      projectGuid: "",
      selectedProjectName: "",
      isFutureDate: false,
      isFactExceedAllowed: false,

      isLoadingProjects: false,
      isIngestingProjects: false,
      projectGroups: [],
      projectDialog: { show: false },

      isLoading: false,
      isCheckingWarnings: false,
      treeData: [],
      expandedIds: [],

      dateFrom: localStorage.getItem('ppm_date_from') || "",
      dateTo:   localStorage.getItem('ppm_date_to')   || "",
      periodRange: (() => {
        const f = localStorage.getItem('ppm_date_from');
        const t = localStorage.getItem('ppm_date_to');
        return f && t ? { from: f, to: t } : null;
      })(),
      calendarDates: [],

      cellValues:     {},
      cellMeta:       {},
      workFactPctMap: {},
      workPlanMap: {},
      cumulativeFactSums: {},
      completedWorks: new Set(),
      gprPeriodsByName: {},
      smetaChangeLog: {},

      filterType:        null,
      filterContractor:  null,
      projectTypeOptions: [],
      contractorOptions:  [],
      gprLoadingGuids: {},
      floorLoadingIds: {},
      blockLoadingIds: {},
      expandAllProgress: { active: false, done: 0, total: 0 },
      _expandAllCancelled: false,
      isExpandedAll: false,
      _suppressCascade: false,
      stornoMode: false,
      /** Защита от гонки async при быстром переключении даты/диалога */
      _ppmFactDlgLoadId: 0,
      _ppmGfdLoadId:     0,

      colWidths: {
        node:   parseInt(localStorage.getItem('ppm_col_node')   || '360', 10),
        amount: parseInt(localStorage.getItem('ppm_col_amount') || '100', 10),
        fact:   parseInt(localStorage.getItem('ppm_col_fact')   || '110', 10),
      },
      _colResize: null,

      jsonDialog: { show: false, content: "", actionType: "", workGuids: [], factIds: [], unsentFactIds: [], sending: false, perPeriod: true },
      hasUspActionGrant: false,
      hasStornoGrant: false,
      hasStornoListGrant: false,

      factDialog: {
        show:          false,
        row:           null,
        date:          null,
        loading:       false,
        factValue:        '',
        factPct:          0,
        materialsOpen:    false,
        matSyncFact:      false,
        saving:           false,
        deleting:         false,
        workData:         null,
        isSent:           false,
        minFactValue:     0,
        uspFactId:        null,
        factPrice:       null,
        factPriceOrigin: null,
        materialsPrice:  0,
        isStornoMode:    false,
        originalFactValue: '',
        newFactValue:     '',
        stornoDiff:       0,
        originalMaterials:        [],
        correctionHistory:        [],
        correctionHistoryLoading: false,
      },

      groupFactDialog: {
        show:      false,
        groupRow:  null,
        date:      null,
        loading:   false,
        saving:    false,
        workItems: [],
      },

      selectedWorkIds: [],

      completeDialog: {
        show:     false,
        rows:     [],
        loading:  false,
      },

      alignDialog: {
        show:     false,
        title:    'Выравнить факт по смете',
        summary:  null,
        busy:     false,
        applying: false,
        error:    '',
        combos:   [],
        rows:     [],
      },

      factHistoryDialog: {
        show:     false,
        title:    '',
        loading:  false,
        rows:     [],
        totalSmr: 0,
        totalTmc: 0,
        factPct:  0,
        nodeType: '',
        filterType:  null,
        filterBlock: null,
        filterWork:  '',
      },

      smetaChangeDialog: { show: false, workName: '', info: null },

      uspSending:      false,
      fillGuidLoading: false,
      showFullNames:   window.localStorage.getItem('ppm_showFullNames') === 'true',
      isFullscreen:    false,
      _vScrollTop:     0,
      _vViewportH:     800,

      uspDialog: {
        show:           false,
        loading:        false,
        loadingData:    false,
        savedRows:      [],
        workGroups:     [],
        stornoSelected: [],
        jsonContent:    '',
      },
      uspReportDialog: {
        show:        false,
        loading:     false,
        refreshing:  false,
        rows:        [],
        sort:        { col: null, dir: 'asc' },
        search:      '',
        dateFrom:    '',
        dateTo:      '',
        filterType:  null,
        filterBlock: null,
        filterFloor: null,
      },
      uspWorkDetailDialog: {
        show:      false,
        loading:   false,
        workName:  '',
        workGuid:  '',
        facts:     [],
        materials: [],
      },
      uspGuidDetailDialog: {
        show:     false,
        loading:  false,
        uspGuid:  '',
        rows:     [],
      },
      discrepancyDialog: {
        show: false,
        loading: false,
        refreshing: false,
        rows: [],
        search: '',
        filterType: null,
        filterBlock: null,
        filterFloor: null,
        filterSource: 'all',
      },
      stornoListDialog: {
        show: false,
        loading: false,
        rows: [],
        factItems: [],
        presetFilterType: null,
      },
      discrepancyDetailDialog: {
        show: false,
        loading: false,
        workGuid: '',
        workName: '',
        rows: [],
      },
    };
  },
  computed: {
    allExpanded() {
      return this.isExpandedAll;
    },

    periodLabel() {
      if (!this.dateFrom || !this.dateTo) return "";
      return `${this.dateFrom} — ${this.dateTo}`;
    },

    todayIso() {
      const dt = new Date();
      return (
        dt.getFullYear() +
        "-" +
        String(dt.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(dt.getDate()).padStart(2, "0")
      );
    },

    // fhTypeOptions, fhBlockOptions, fhFilteredRows, fhGroupedRows, fhTotalSmrFiltered, fhTotalTmcFiltered — moved to PpmFactHistoryDialog

    // uspReportTypeOptions, uspReportBlockOptions, uspReportFloorOptions, uspReportFilteredRows, uspReportSummary — moved to PpmUspReportDialog
    // discTypeOptions, discBlockOptions, discFloorOptions, discFilteredRows, discSummary — moved to PpmDiscrepancyDialog

    expandedSet() {
      return new Set(this.expandedIds);
    },

    visibleRows() {
      const rows = [];
      const expSet = this.expandedSet;
      const traverse = (nodes, depth, parentVisible) => {
        for (const n of (nodes || [])) {
          const hasChildren = n.hasWorks === false
            ? false
            : n.node_type === 'floor'
              ? (!n.worksLoaded || !!(n.children && n.children.length))
              : !!(n.children && n.children.length);
          if (parentVisible) {
            n._depth = depth;
            n._hasChildren = hasChildren;
            rows.push(n);
          }
          if (n.children && n.children.length) {
            traverse(n.children, depth + 1, parentVisible && expSet.has(n.id));
          }
        }
      };
      traverse(this.treeData, 0, true);
      return rows;
    },

    _vRowHeight()  { return 44; },
    _vOverscan()   { return 15; },
    _vTotalHeight() { return this.visibleRows.length * this._vRowHeight; },
    _vStartIdx() {
      return Math.max(0, Math.floor(this._vScrollTop / this._vRowHeight) - this._vOverscan);
    },
    _vEndIdx() {
      const viewportH = this._vViewportH || 800;
      return Math.min(
        this.visibleRows.length,
        Math.ceil((this._vScrollTop + viewportH) / this._vRowHeight) + this._vOverscan
      );
    },
    slicedRows() {
      const start = this._vStartIdx;
      const slice = this.visibleRows.slice(start, this._vEndIdx);
      for (let i = 0; i < slice.length; i++) {
        slice[i]._vIdx = start + i;
      }
      return slice;
    },
    _vTopSpacerHeight() { return this._vStartIdx * this._vRowHeight; },
    _vBottomSpacerHeight() { return (this.visibleRows.length - this._vEndIdx) * this._vRowHeight; },

    monthGroups() {
      const groups = [];
      let prev = null;
      this.calendarDates.forEach((d) => {
        if (!prev || prev.key !== d.monthKey) {
          prev = { key: d.monthKey, label: d.monthLabel, count: 1 };
          groups.push(prev);
        } else {
          prev.count += 1;
        }
      });
      return groups;
    },

    // Все work-узлы из дерева, независимо от того, свёрнуты ли родители
    allWorkNodes() {
      const result = [];
      const traverse = (nodes) => {
        (nodes || []).forEach((n) => {
          if (n.node_type === 'work') result.push(n);
          if (n.children && n.children.length) traverse(n.children);
        });
      };
      traverse(this.treeData);
      return result;
    },

    hierarchyPctMap() {
      const floorIndex = {};
      for (const rowId of Object.keys(this.workPlanMap)) {
        const m = rowId.match(/^work_(\d+)_/);
        if (m) {
          const fid = m[1];
          if (!floorIndex[fid]) floorIndex[fid] = [];
          floorIndex[fid].push(rowId);
        }
      }
      const result = {};
      const calcForNode = (node) => {
        const floorIds = [];
        const collectFloors = (n) => {
          if (n.node_type === 'floor') { floorIds.push(n.floor_map_id); return; }
          for (const c of (n.children || [])) collectFloors(c);
        };
        if (node.node_type === 'floor') floorIds.push(node.floor_map_id);
        else collectFloors(node);
        if (!floorIds.length) { result[node.id] = null; return; }

        let totalFact = 0, totalPlan = 0;
        for (const fid of floorIds) {
          for (const wid of (floorIndex[fid] || [])) {
            totalFact += this.cumulativeFactSums[wid] || 0;
            totalPlan += this.workPlanMap[wid] || 0;
          }
        }
        result[node.id] = totalPlan > 0 ? Math.round(totalFact / totalPlan * 1000) / 10 : 0;
      };
      const walk = (nodes) => {
        for (const n of (nodes || [])) {
          if (n.node_type !== 'work') calcForNode(n);
          if (n.children?.length) walk(n.children);
        }
      };
      walk(this.treeData);
      return result;
    },

    groupCompletionMap() {
      const result = {};
      const walk = (nodes) => {
        for (const n of (nodes || [])) {
          if (n.node_type === 'group') {
            const children = n.children || [];
            if (!children.length) {
              result[n.id] = 'none';
            } else {
              const done = children.filter((c) => this.completedWorks.has(
                `${c.floor_map_id}_${c.work_set_id}_${c.work_set_check_group_id}`
              )).length;
              result[n.id] = done === 0 ? 'none' : done === children.length ? 'all' : 'partial';
            }
          }
          if (n.children?.length) walk(n.children);
        }
      };
      walk(this.treeData);
      return result;
    },

    parentCellDataMap() {
      const floorWorkIndex = {};
      const addToIndex = (store) => {
        for (const rowId of Object.keys(store)) {
          const m = rowId.match(/^work_(\d+)_/);
          if (m) {
            const fid = m[1];
            if (!floorWorkIndex[fid]) floorWorkIndex[fid] = new Set();
            floorWorkIndex[fid].add(rowId);
          }
        }
      };
      addToIndex(this.cellValues);
      addToIndex(this.cellMeta);

      const result = {};
      const dates = this.calendarDates;

      const processNode = (node) => {
        if (node.node_type === 'work') return;
        const nodeData = {};
        let workRowIds;
        if (node.node_type === 'group') {
          workRowIds = (node.children || []).map(c => c.id);
        } else {
          const floorIds = [];
          const collectFloors = (n) => {
            if (n.node_type === 'floor') { floorIds.push(n.floor_map_id); return; }
            (n.children || []).forEach(collectFloors);
          };
          if (node.node_type === 'floor') floorIds.push(node.floor_map_id);
          else (node.children || []).forEach(collectFloors);
          const idSet = new Set();
          for (const fid of floorIds) {
            for (const wid of (floorWorkIndex[fid] || [])) idSet.add(wid);
          }
          workRowIds = [...idSet];
        }
        for (const d of dates) {
          let smr = 0, tmc = 0, hasData = false;
          for (const wid of workRowIds) {
            const cv = this.cellValues?.[wid]?.[d.key];
            const cm = this.cellMeta?.[wid]?.[d.key];
            if (cv != null || cm != null) {
              hasData = true;
              if (cm?.factPrice) smr += cm.factPrice;
              if (cm?.materialsPriceSmr) smr += cm.materialsPriceSmr;
              if (cm?.materialsPriceTmc) tmc += cm.materialsPriceTmc;
            }
          }
          if (hasData) nodeData[d.key] = { smr, tmc, hasData: true };
        }
        result[node.id] = nodeData;
      };
      const walk = (nodes) => {
        for (const n of (nodes || [])) {
          processNode(n);
          if (n.children?.length) walk(n.children);
        }
      };
      walk(this.treeData);
      return result;
    },

    canSend() {
      return !!this.projectGuid && !!this.dateFrom && !!this.dateTo && this.visibleRows.length > 0;
    },

    /**
     * Функциональный минимум ползунка группы (0–100).
     * = sum(cumulative_fact + minFactValue) / sum(plan) × 100
     * cumulative_fact — выполнено на ДРУГИХ датах
     * minFactValue    — уже сохранённое значение за ЭТОТ день (нельзя уменьшать)
     */
    /** Итого KZT из БД = работа + материалы */
    factDialogKztTotal() {
      const work = this.factDialog.factPrice;
      if (work == null) return null;
      return Math.round((work + (this.factDialog.materialsPrice || 0)) * 100) / 100;
    },

    groupCumulativePct() {
      const items   = this.groupFactDialog.workItems;
      const totPlan = items.reduce((s, i) => s + (i.workData?.plan_total || 0), 0);
      const totMin  = items.reduce((s, i) =>
        s + (i.workData?.cumulative_fact || 0) + (i.minFactValue || 0), 0);
      return totPlan > 0 ? Math.min(100, Math.round((totMin / totPlan) * 100)) : 0;
    },

    groupFactOverallPct() {
      const items   = this.groupFactDialog.workItems || [];
      const totPlan = items.reduce((s, i) => s + (i.workData?.plan_total || 0), 0);
      if (totPlan <= 0) return 0;
      const totDone = items.reduce((s, i) => {
        const cum  = i.workData?.cumulative_fact || 0;
        const fact = this.parseFactNum(i.factValue);
        return s + cum + fact;
      }, 0);
      return Math.min(100, Math.round((totDone / totPlan) * 100));
    },

    groupLockIconPct() {
      return Math.min(96, this.groupCumulativePct);
    },

    /** Есть ли работы, которые ещё можно редактировать (не УСП и не завершено) */
    groupFactDialogHasSavableWorks() {
      const items = this.groupFactDialog.workItems || [];
      return items.some((item) => item.workData && !item.isSent && !item.workData.is_complete);
    },

  },
  async mounted() {
    this.loadProjects();
    this.clampDatesToToday();
    getHasGrant('M__ProjectPatternUspAction').then(v => { this.hasUspActionGrant = v === true; });
    getHasGrant(STORNO_GRANT).then(v => { this.hasStornoGrant = v === true; });
    getHasGrant(STORNO_LIST_GRANT).then(v => { this.hasStornoListGrant = v === true; });
    if (this.dateFrom && this.dateTo) this.buildCalendar();
    this._onEsc = (e) => {
      if (e.key === 'Escape' && this.isFullscreen) this.toggleFullscreen();
    };
    document.addEventListener('keydown', this._onEsc);
    this.$nextTick(() => {
      if (this.$refs.tableWrap) this._vViewportH = this.$refs.tableWrap.clientHeight || 800;
    });
  },
  beforeUnmount() {
    if (this._tableObserver) this._tableObserver.disconnect();
    if (this.isFullscreen) document.body.style.overflow = this._prevOverflow || '';
    document.removeEventListener('keydown', this._onEsc);
  },
  watch: {
    hasStornoGrant(val) {
      if (!val) this.stornoMode = false;
    },
    filterType(val) {
      if (!this.projectGuid) return;
      if (!this._suppressCascade) {
        if (val !== null) this.reloadContractorsByType(val);
        else this.reloadContractors();
      }
      this._scheduleReload();
    },
    filterContractor(val) {
      if (!this.projectGuid) return;
      if (!this._suppressCascade) {
        if (val !== null) this.reloadProjectTypesByContractor(val);
        else this.reloadProjectTypes();
      }
      this._scheduleReload();
    },
    periodRange: {
      deep: true,
      handler(val) {
        if (!val) return;
        const from = val.from || null;
        const to   = val.to   || null;
        if (from && to) {
          this.dateFrom = from;
          this.dateTo   = to;
          this.clampDatesToToday();
          localStorage.setItem('ppm_date_from', this.dateFrom);
          localStorage.setItem('ppm_date_to',   this.dateTo);
          this.buildCalendar();
          if (this.projectGuid) {
            this.loadStructure();
          }
        }
      },
    },
  },
  methods: {
    // ──────────────────────────────────────────────
    //  Скролл-синхронизация
    // ──────────────────────────────────────────────
    isWorkComplete(row) {
      return this.completedWorks.has(
        `${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id}`
      );
    },

    _scheduleReload() {
      clearTimeout(this._filterDebounce);
      this._filterDebounce = setTimeout(() => this.loadStructure(), 500);
    },

    onWrapScroll() {},

    onTableWrapScroll(e) {
      this._vScrollTop = e.target.scrollTop;
      const h = e.target.clientHeight;
      if (h && h !== this._vViewportH) this._vViewportH = h;
    },

    // ──────────────────────────────────────────────
    //  Resize колонок
    // ──────────────────────────────────────────────
    startColResize(e, col) {
      const startX    = e.clientX;
      const startW    = this.colWidths[col];
      const minW      = col === 'node' ? 160 : 60;

      const onMove = (mv) => {
        const newW = Math.max(minW, startW + mv.clientX - startX);
        this.colWidths = { ...this.colWidths, [col]: newW };
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor  = '';
        document.body.style.userSelect = '';
        localStorage.setItem('ppm_col_' + col, String(this.colWidths[col]));
      };

      document.body.style.cursor     = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup',   onUp);
    },

    // ──────────────────────────────────────────────
    //  Загрузка проектов
    // ──────────────────────────────────────────────
    async loadProjects() {
      try {
        this.isLoadingProjects = true;
        const res = await getProjectGroups();
        this.projectGroups = Array.isArray(res) ? res : [];
      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Не удалось загрузить список проектов' });
      } finally {
        this.isLoadingProjects = false;
      }
    },
    async onIngestProjects() {
      try {
        this.isIngestingProjects = true;
        await ingestProjectGroups();
        await this.loadProjects();
        this.$q?.notify?.({ type: 'positive', message: 'Список проектов обновлён' });
      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Не удалось обновить список' });
      } finally {
        this.isIngestingProjects = false;
      }
    },
    selectProject(p) {
      this.projectGuid         = p.project_guid;
      this.selectedProjectName = p.project_name;
      this.isFutureDate          = !!p.is_future_date;
      this.isFactExceedAllowed  = !!p.is_fact_exceed_allowed;
      this.filterContractor    = null;
      this.filterType          = null;
      this.contractorOptions   = [];
      this.projectTypeOptions  = [];
      this.projectDialog.show  = false;
      this.clampDatesToToday();
      if (this.dateFrom && this.dateTo) {
        this.loadStructure();
      }
    },
    clearProject() {
      this.projectGuid         = "";
      this.selectedProjectName = "";
      this.isFutureDate          = false;
      this.isFactExceedAllowed  = false;
      this.treeData            = [];
      this.expandedIds         = [];
      this.isExpandedAll       = false;
      this.cellValues               = {};
      this.cellMeta                 = {};
      this.workFactPctMap           = {};
      this.workPlanMap              = {};
      this.cumulativeFactSums       = {};
      this.gprPeriodsByName         = {};
      this.smetaChangeLog           = {};
        this.filterType         = null;
      this.filterContractor   = null;
      this.projectTypeOptions = [];
      this.contractorOptions  = [];
      this.gprLoadingGuids     = {};
      this.floorLoadingIds     = {};
      this.blockLoadingIds     = {};
        this.blockLoadingIds     = {};
    },

    // ──────────────────────────────────────────────
    //  Загрузка структуры (Master-дерево)
    // ──────────────────────────────────────────────
    async loadStructure() {
      if (!this.projectGuid) return;
      try {
        this.isLoading = true;
        const typeName   = this.filterType
          ? (this.projectTypeOptions.find((t) => t.id === this.filterType)?.name || null)
          : null;
        const projectRes = await projectPatternReadFiltered(
          this.projectGuid,
          typeName,
          this.filterContractor || null,
        );
        this.treeData = this.buildProjectTree(Array.isArray(projectRes) ? projectRes : []);
        this.expandedIds      = [];
        this.isExpandedAll    = false;
        this._vScrollTop      = 0;
        if (this.$refs.tableWrap) this.$refs.tableWrap.scrollTop = 0;
        this.cellValues       = {};
        this.cellMeta         = {};
        this.workFactPctMap   = {};
        this.workPlanMap      = {};
        this.cumulativeFactSums = {};
        this.gprPeriodsByName = {};
        this.gprLoadingGuids  = {};
        this.floorLoadingIds  = {};

        // Разворачиваем первый уровень (резиденты)
        for (const root of this.treeData) {
          this.expandedIds.push(root.id);
        }

        const cumulativePromise = this.loadCumulativeFacts();
        this.loadSmetaChangeLog();
        this.preCheckWorkWarnings();
        if (this.dateFrom && this.dateTo) await this.loadSavedValues();

        // Загружаем опции фильтров с учётом каскада
        const [projectTypes, contractors] = await Promise.all([
          this.filterContractor
            ? getProjectTypesByContractor(this.projectGuid, this.filterContractor)
            : getProjectTypes(this.projectGuid),
          getContractorsSMR(this.projectGuid, this.filterType || null),
        ]);
        this.projectTypeOptions = (projectTypes || []).map((pt) => ({
          id:   pt.project_type_id,
          name: pt.project_type_name,
        }));
        const seenC   = new Set();
        const uniqueC = [];
        (contractors || []).forEach((c) => {
          if (!seenC.has(c.contractor_id)) {
            seenC.add(c.contractor_id);
            uniqueC.push({ id: c.contractor_id, name: c.contractor_name });
          }
        });
        this.contractorOptions = uniqueC;
        await cumulativePromise;
      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Не удалось загрузить структуру проекта' });
      } finally {
        this.isLoading = false;
      }
    },

    // ──────────────────────────────────────────────
    //  Каскадная перезагрузка фильтров
    // ──────────────────────────────────────────────
    async reloadProjectTypesByContractor(contractorId) {
      const rows = await getProjectTypesByContractor(this.projectGuid, contractorId);
      const opts = (rows || []).map((pt) => ({ id: pt.project_type_id, name: pt.project_type_name }));
      this.projectTypeOptions = opts;
      if (this.filterType !== null && !opts.find((o) => o.id === this.filterType)) {
        this._suppressCascade = true;
        this.filterType = null;
        this.$nextTick(() => { this._suppressCascade = false; });
      }
    },

    async reloadContractorsByType(projectTypeId) {
      const rows = await getContractorsSMR(this.projectGuid, projectTypeId);
      const seenC = new Set();
      const opts  = [];
      (rows || []).forEach((c) => {
        if (!seenC.has(c.contractor_id)) {
          seenC.add(c.contractor_id);
          opts.push({ id: c.contractor_id, name: c.contractor_name });
        }
      });
      this.contractorOptions = opts;
      if (this.filterContractor !== null && !opts.find((o) => o.id === this.filterContractor)) {
        this._suppressCascade = true;
        this.filterContractor = null;
        this.$nextTick(() => { this._suppressCascade = false; });
      }
    },

    async reloadProjectTypes() {
      const rows = await getProjectTypes(this.projectGuid);
      this.projectTypeOptions = (rows || []).map((pt) => ({ id: pt.project_type_id, name: pt.project_type_name }));
    },

    async reloadContractors() {
      const rows = await getContractors(this.projectGuid);
      const seenC = new Set();
      const opts  = [];
      (rows || []).forEach((c) => {
        if (!seenC.has(c.contractor_id)) {
          seenC.add(c.contractor_id);
          opts.push({ id: c.contractor_id, name: c.contractor_name });
        }
      });
      this.contractorOptions = opts;
    },

    buildProjectTree(data) {
      if (!data.length) return [];
      const residentsMap = new Map();

      data.forEach((item) => {
        const resKey = item.resident_name || 'Без резидента';
        if (!residentsMap.has(resKey)) {
          residentsMap.set(resKey, {
            id: `resident_${item.resident_id || resKey}`,
            label: resKey,
            node_type: 'resident',
            children: [],
            _typesMap: new Map(),
          });
        }
        const resident = residentsMap.get(resKey);

        const typeKey = `${resKey}__type_${item.project_type_id}`;
        if (!resident._typesMap.has(typeKey)) {
          const pt = {
            id: typeKey,
            label: item.project_type_name || `Тип ${item.project_type_id}`,
            node_type: 'project_type',
            children: [],
            _blocksMap: new Map(),
          };
          resident._typesMap.set(typeKey, pt);
          resident.children.push(pt);
        }
        const projectType = resident._typesMap.get(typeKey);

        const blockKey = `entrance_${item.project_entrance_id}`;
        if (!projectType._blocksMap.has(blockKey)) {
          const block = {
            id: blockKey,
            label: `Блок ${item.block_name} (Подъезд ${item.entrance})`,
            node_type: 'block',
            project_entrance_id: item.project_entrance_id,
            usp_block_guid: item.usp_block_guid || null,
            project_id: item.project_id,
            project_type_name: item.project_type_name || '',
            children: [],
            _floorsMap: new Map(),
          };
          projectType._blocksMap.set(blockKey, block);
          projectType.children.push(block);
        }
        const block = projectType._blocksMap.get(blockKey);

        const floorKey = `floor_${item.floor_map_id}`;
        if (!block._floorsMap.has(floorKey)) {
          block._floorsMap.set(floorKey, {
            id: floorKey,
            label: `Этаж ${item.level_num}`,
            node_type: 'floor',
            floor_map_id: item.floor_map_id,
            usp_level_guid: item.usp_level_guid || null,
            usp_block_guid: item.usp_block_guid || null,
            project_entrance_id: item.project_entrance_id,
            project_id: item.project_id,
            level_num: item.level_num,
            usp_work_guid: null,
            hasWorks: item.has_works !== false,
            hasWarning: false,
            children: [],
            worksLoaded: false,
          });
          block.children.push(block._floorsMap.get(floorKey));
        }
      });

      // Сортируем, чистим Maps и прокидываем hasWorks / hasWarning вверх
      residentsMap.forEach((resident) => {
        resident.children.forEach((pt) => {
          pt.children.forEach((block) => {
            block.children.sort((a, b) => a.level_num - b.level_num);
            block.hasWorks = block.children.some((f) => f.hasWorks);
            block.hasWarning = false;
            delete block._floorsMap;
          });
          pt.hasWorks = pt.children.some((b) => b.hasWorks);
          pt.hasWarning = false;
          delete pt._blocksMap;
        });
        resident.hasWorks = resident.children.some((pt) => pt.hasWorks);
        resident.hasWarning = false;
        delete resident._typesMap;
      });

      return Array.from(residentsMap.values());
    },

    // ──────────────────────────────────────────────
    //  Разворот / сворачивание
    // ──────────────────────────────────────────────
    async toggleExpand(id) {
      const idx = this.expandedIds.indexOf(id);
      if (idx > -1) {
        this.expandedIds.splice(idx, 1);
      } else {
        const node = this.findNodeById(this.treeData, id);
        if (node && node.hasWorks === false) return;
        if (node?.node_type === 'floor' && !node.worksLoaded) {
          await this.loadWorksForFloor(node);
        }
        if (node?.node_type === 'block') {
          await this.loadGprForFloors(node, id);
        }
        this.expandedIds.push(id);
      }
    },

    findNodeById(nodes, id) {
      for (const n of (nodes || [])) {
        if (n.id === id) return n;
        const found = this.findNodeById(n.children, id);
        if (found) return found;
      }
      return null;
    },

    findParentNode(nodes, childId, parent) {
      for (const n of (nodes || [])) {
        if (n.id === childId) return parent;
        const found = this.findParentNode(n.children, childId, n);
        if (found) return found;
      }
      return null;
    },

    // ──────────────────────────────────────────────
    //  Работы (ленивая загрузка)
    // ──────────────────────────────────────────────
    async loadWorksForFloor(floorNode, { silent = false } = {}) {
      try {
        if (!silent) this.floorLoadingIds = { ...this.floorLoadingIds, [floorNode.id]: true };

        const [res, workGuid] = await Promise.all([
          getWorkSetsForMatrix(floorNode.floor_map_id),
          (floorNode.usp_level_guid && floorNode.usp_block_guid)
            ? getWorkGuidByProject(
                this.projectGuid,
                floorNode.usp_block_guid,
                floorNode.usp_level_guid,
                floorNode.project_id,
              )
            : Promise.resolve(null),
        ]);

        floorNode.usp_work_guid = workGuid || null;

        const groups = (res || []).map((grp) => ({
          id: `group_${floorNode.floor_map_id}_${grp.work_set_check_group_id}`,
          label: grp.work_set_check_group_name,
          node_type: 'group',
          floor_map_id: floorNode.floor_map_id,
          usp_level_guid: floorNode.usp_level_guid,
          children: (grp.work_sets || []).map((ws) => ({
            id: `work_${floorNode.floor_map_id}_${ws.work_set_id}`,
            label: ws.work_set_name,
            fullLabel: `${ws.work_set_id} / ${ws.work_set_name} / ${ws.work_set_name_agreement || ws.work_set_name}`,
            work_set_name_agreement: ws.work_set_name_agreement || ws.work_set_name,
            node_type: 'work',
            floor_map_id: floorNode.floor_map_id,
            usp_level_guid: floorNode.usp_level_guid,
            usp_work_guid: workGuid || null,
            work_set_id: ws.work_set_id,
            work_set_check_group_id: grp.work_set_check_group_id || null,
            unit_name: ws.unit_name || null,
            work_set_amount: ws.work_set_amount ?? 0,
            children: [],
          })),
        })).filter((g) => g.children.length);

        floorNode.children    = groups;
        floorNode.worksLoaded = true;

        if (!silent) this.treeData = [...this.treeData];
      } catch {
        floorNode.children    = [];
        floorNode.worksLoaded = true;
        if (!silent) this.treeData = [...this.treeData];
      } finally {
        if (!silent) {
          const l = { ...this.floorLoadingIds };
          delete l[floorNode.id];
          this.floorLoadingIds = l;
        }
      }
    },

    // ──────────────────────────────────────────────
    //  Фоновая проверка привязок (один запрос на бэк)
    //  Проверяет: блоки, этажи, работы
    // ──────────────────────────────────────────────
    async preCheckWorkWarnings() {
      const checkId = (this._preCheckId = (this._preCheckId || 0) + 1);

      await new Promise((r) => setTimeout(r, 300));
      if (this._preCheckId !== checkId) return;

      this.isCheckingWarnings = true;
      try {
        const rows = await checkWorkGuidsBatch(this.projectGuid);
        if (this._preCheckId !== checkId) return;
        if (!rows?.length) return;

        const floorMap = new Map();
        const blockHasNoGuid = new Set();
        for (const r of rows) {
          floorMap.set(r.floor_map_id, {
            usp_work_guid:  r.usp_work_guid || null,
            usp_level_guid: r.usp_level_guid || null,
            usp_block_guid: r.usp_block_guid || null,
          });
          if (!r.usp_block_guid) blockHasNoGuid.add(r.project_entrance_id);
        }

        const setWarningsBottomUp = (nodes) => {
          for (const n of (nodes || [])) {
            if (n.children?.length) setWarningsBottomUp(n.children);

            if (n.node_type === 'floor') {
              const info = floorMap.get(n.floor_map_id);
              if (info) {
                n.usp_work_guid = info.usp_work_guid;
                n.hasWarning = !info.usp_level_guid || !info.usp_block_guid || !info.usp_work_guid;
              }
            } else if (n.node_type === 'block') {
              const ownWarn = blockHasNoGuid.has(n.project_entrance_id);
              const childWarn = (n.children || []).some((c) => c.hasWarning);
              n.hasWarning = ownWarn || childWarn;
            } else {
              n.hasWarning = (n.children || []).some((c) => c.hasWarning);
            }
          }
        };
        setWarningsBottomUp(this.treeData);
        this.treeData = [...this.treeData];
      } catch { /* ignore */ }
      finally {
        if (this._preCheckId === checkId) this.isCheckingWarnings = false;
      }
    },

    // ──────────────────────────────────────────────
    //  Заполнение usp_work_guid перед отправкой
    // ──────────────────────────────────────────────
    // Находит все floor-узлы с usp_work_guid = null,
    // пробует разрешить через API и обновляет дерево.
    // Возвращает { resolved, failed, skipped }:
    //   resolved — удалось получить guid
    //   failed   — API вернул null (нет совпадения в УСП)
    //   skipped  — нет usp_level_guid/usp_block_guid (нечего отправлять в API)
    async resolveUspWorkGuids() {
      const floorNodes = [];
      const walk = (nodes) => {
        for (const n of (nodes || [])) {
          if (n.node_type === 'floor' && !n.usp_work_guid) floorNodes.push(n);
          if (n.children?.length) walk(n.children);
        }
      };
      walk(this.treeData);

      if (!floorNodes.length) return { resolved: 0, failed: 0, skipped: 0 };

      let resolved = 0, failed = 0, skipped = 0;

      await Promise.allSettled(
        floorNodes.map(async (floor) => {
          if (!floor.usp_level_guid || !floor.usp_block_guid) {
            skipped++;
            return;
          }
          try {
            const guid = await getWorkGuidByProject(
              this.projectGuid,
              floor.usp_block_guid,
              floor.usp_level_guid,
              floor.project_id,
            );
            if (guid) {
              floor.usp_work_guid = guid;
              // обновить все work-узлы этого этажа
              const updateWorks = (nodes) => {
                for (const n of (nodes || [])) {
                  if (n.node_type === 'work' && n.floor_map_id === floor.floor_map_id) {
                    n.usp_work_guid = guid;
                  }
                  if (n.children?.length) updateWorks(n.children);
                }
              };
              updateWorks(floor.children);
              resolved++;
            } else {
              failed++;
            }
          } catch {
            failed++;
          }
        }),
      );

      // триггер реактивности
      this.treeData = [...this.treeData];

      return { resolved, failed, skipped };
    },

    async expandAll() {
      if (this.expandAllProgress.active) return;

      const topIds = [];
      const floors = [];
      const blocks = [];
      const collectTop = (nodes) => {
        (nodes || []).forEach((n) => {
          if (n.node_type !== 'work') {
            topIds.push(n.id);
            if (n.node_type === 'floor' && !n.worksLoaded) floors.push(n);
            if (n.node_type === 'block') blocks.push({ node: n, id: n.id });
            if (n.children?.length) collectTop(n.children);
          }
        });
      };
      collectTop(this.treeData);

      this.isExpandedAll = true;
      this.expandedIds   = [...topIds];

      if (!floors.length && !blocks.length) return;

      const total = floors.length + blocks.length;
      const BATCH = 4;
      this._expandAllCancelled = false;
      this.expandAllProgress = { active: true, done: 0, total };

      for (let i = 0; i < blocks.length; i += BATCH) {
        if (this._expandAllCancelled) break;
        await Promise.all(
          blocks.slice(i, i + BATCH).map(({ node, id }) => this.loadGprForFloors(node, id))
        );
        if (this._expandAllCancelled) break;
        this.expandAllProgress = { ...this.expandAllProgress, done: Math.min(i + BATCH, blocks.length) };
      }

      const newGroupIds = [];
      for (let i = 0; i < floors.length; i += BATCH) {
        if (this._expandAllCancelled) break;
        await Promise.all(floors.slice(i, i + BATCH).map((f) => this.loadWorksForFloor(f, { silent: true })));
        if (this._expandAllCancelled) break;
        floors.slice(i, i + BATCH).forEach((f) => {
          (f.children || []).forEach((grp) => {
            newGroupIds.push(grp.id);
          });
        });
        this.expandAllProgress = {
          ...this.expandAllProgress,
          done: blocks.length + Math.min(i + BATCH, floors.length),
        };
      }

      this.treeData    = [...this.treeData];
      this.expandedIds = [...this.expandedIds, ...newGroupIds];

      this.expandAllProgress    = { active: false, done: 0, total: 0 };
      this._expandAllCancelled  = false;
    },
    collapseAll() {
      this._expandAllCancelled = true;
      this.isExpandedAll = false;
      this.expandedIds   = [];
      for (const root of this.treeData) {
        this.expandedIds.push(root.id);
      }
      this.expandAllProgress = { active: false, done: 0, total: 0 };
    },

    async refreshNodes() {
      if (this.expandAllProgress.active) return;
      if (!this.projectGuid) return;

      // Полное обновление: ведём себя как при перевыборе проекта —
      // сбрасываем кешированные данные и перегружаем всё с нуля.
      this.treeData              = [];
      this.expandedIds           = [];
      this.isExpandedAll         = false;
      this.cellValues            = {};
      this.cellMeta              = {};
      this.workFactPctMap        = {};
      this.workPlanMap           = {};
      this.cumulativeFactSums    = {};
      this.gprPeriodsByName      = {};
      this.smetaChangeLog        = {};
      this.gprLoadingGuids       = {};
      this.floorLoadingIds       = {};
      this.blockLoadingIds       = {};

      if (this.dateFrom && this.dateTo) {
        await this.loadStructure();
      }
    },

    // ──────────────────────────────────────────────
    //  ГПР (базовый / текущий период)
    // ──────────────────────────────────────────────
    gprKeyForRow(row) {
      if (row?.node_type === 'floor' && row.usp_level_guid) return String(row.usp_level_guid);
      return null;
    },

    async loadGprForFloors(blockNode, blockId) {
      if (!blockNode?.children?.length) return;
      const guids = new Set();
      blockNode.children.forEach((floor) => {
        const key = floor.usp_level_guid;
        if (key && !this.gprPeriodsByName[key]) guids.add(key);
      });
      if (!guids.size) return;

      // Спиннер на кнопке блока
      if (blockId) this.blockLoadingIds = { ...this.blockLoadingIds, [blockId]: true };

      const loading = {};
      guids.forEach((k) => { loading[k] = true; });
      this.gprLoadingGuids = { ...this.gprLoadingGuids, ...loading };

      try {
        const periods = await getGprPeriods(this.projectGuid, Array.from(guids));
        const add = {};
        (Array.isArray(periods) ? periods : []).forEach((p) => {
          if (!p?.usp_level_guid) return;
          add[p.usp_level_guid] = p;
        });
        this.gprPeriodsByName = { ...this.gprPeriodsByName, ...add };
      } catch {
        // тихо
      } finally {
        const done = { ...this.gprLoadingGuids };
        guids.forEach((k) => { delete done[k]; });
        this.gprLoadingGuids = done;
        if (blockId) {
          const bl = { ...this.blockLoadingIds };
          delete bl[blockId];
          this.blockLoadingIds = bl;
        }
      }
    },

    // round6, r6 — provided by useFormatters via setup()
    visibleMatIndex(materials, idx) {
      let n = 0;
      for (let i = 0; i < idx; i++) {
        if (materials[i].isShow !== false) n++;
      }
      return n + 1;
    },
    // parseFactNum — provided by useFormatters via setup()
    // fmtIsoToRu, fmtRange — provided by useFormatters via setup()
    getGprText(row, kind) {
      const key = this.gprKeyForRow(row) || "";
      const p   = this.gprPeriodsByName?.[key];
      if (!p) return "—";
      if (kind === "base")    return this.fmtRange(p.base_from, p.base_to);
      if (kind === "current") return this.fmtRange(p.cur_from,  p.cur_to);
      return "—";
    },
    getGprShort(row, kind) {
      const key = this.gprKeyForRow(row) || "";
      const p   = this.gprPeriodsByName?.[key];
      if (!p) return "—";
      const fmt = (iso) => {
        if (!iso) return null;
        const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? `${m[3]}.${m[2]}` : null;
      };
      const a = kind === "base"    ? fmt(p.base_from) : fmt(p.cur_from);
      const b = kind === "base"    ? fmt(p.base_to)   : fmt(p.cur_to);
      if (!a && !b) return "—";
      if (!b || a === b) return a || "—";
      return `${a}–${b}`;
    },

    // ──────────────────────────────────────────────
    //  Календарь
    // ──────────────────────────────────────────────
    dateNotFuture(date) {
      if (this.isFutureDate) return true;
      return date <= this.todayIso.replace(/-/g, '/');
    },
    clampDatesToToday() {
      if (this.isFutureDate) return;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const fmt = (dt) => String(dt.getDate()).padStart(2, '0') + '.' + String(dt.getMonth() + 1).padStart(2, '0') + '.' + dt.getFullYear();

      let changed = false;
      if (this.dateTo) {
        const to = this.parseIso(this.dateTo);
        if (to && to > today) {
          this.dateTo = fmt(today);
          changed = true;
        }
      }
      if (this.dateFrom) {
        const from = this.parseIso(this.dateFrom);
        if (from && from > today) {
          this.dateFrom = fmt(today);
          changed = true;
        }
      }
      if (changed) {
        localStorage.setItem('ppm_date_from', this.dateFrom);
        localStorage.setItem('ppm_date_to', this.dateTo);
        this.periodRange = { from: this.dateFrom, to: this.dateTo };
        this.buildCalendar();
      }
    },
    clearDates() {
      this.dateFrom    = "";
      this.dateTo      = "";
      this.periodRange = null;
      this.calendarDates = [];
      localStorage.removeItem('ppm_date_from');
      localStorage.removeItem('ppm_date_to');
    },
    buildCalendar() {
      const from = this.parseIso(this.dateFrom);
      const to   = this.parseIso(this.dateTo);
      if (!from || !to) {
        this.$q?.notify?.({ type: 'warning', message: 'Неверный формат дат' });
        return;
      }
      if (from.getTime() > to.getTime()) {
        this.$q?.notify?.({ type: 'warning', message: 'Дата «с» должна быть ≤ дате «по»' });
        return;
      }
      const maxDays = 365;
      const dates = [];
      const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
      while (cur.getTime() <= to.getTime() && dates.length < maxDays) {
        const iso = this.isoDate(cur);
        const dow = cur.getDay();
        const monthKey = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
        dates.push({
          iso,
          key: `d_${iso.replaceAll("-", "")}`,
          day: cur.getDate(),
          wd:  cur.toLocaleDateString("ru-RU", { weekday: "short" }),
          isWeekend: dow === 0 || dow === 6,
          monthKey,
          monthLabel: cur.toLocaleDateString("ru-RU", { month: "long", year: "numeric" }),
        });
        cur.setDate(cur.getDate() + 1);
      }
      if (dates.length >= maxDays) {
        this.$q?.notify?.({ type: 'warning', message: `Слишком большой период (>${maxDays} дней)` });
      }
      this.calendarDates = dates;
    },

    // ──────────────────────────────────────────────
    //  Ячейки
    // ──────────────────────────────────────────────
    getCellVal(rowId, colKey) {
      return this.cellValues?.[rowId]?.[colKey] ?? "";
    },

    /** Массив строк из ответа uspFactRead (разные обёртки axios) */
    uspFactRowsFromResponse(factRes) {
      if (Array.isArray(factRes)) return factRes;
      if (factRes && Array.isArray(factRes.data)) return factRes.data;
      return [];
    },
    /** Строка факта строго за указанную дату (не [0] — при нескольких строках бралась чужая дата) */
    pickUspFactForDate(factRows, dateIso) {
      if (!dateIso || !factRows?.length) return null;
      const want = String(dateIso).slice(0, 10);
      for (let i = 0; i < factRows.length; i++) {
        const r = factRows[i];
        if (r?.value_date == null) continue;
        const got = String(r.value_date).slice(0, 10);
        if (got === want) return r;
      }
      return null;
    },
    setCellVal(rowId, colKey, v) {
      if (!this.cellValues[rowId]) {
        this.cellValues = { ...this.cellValues, [rowId]: {} };
      }
      this.cellValues[rowId] = { ...this.cellValues[rowId], [colKey]: v === "" ? null : v };
    },
    getFactSum(rowId) {
      const cols = this.cellValues[rowId];
      if (!cols) return 0;
      return Object.values(cols).reduce((s, v) => {
        const n = parseFloat(v);
        return s + (isNaN(n) ? 0 : n);
      }, 0);
    },
    getFactPct(row) {
      const plan = parseFloat(row?.work_set_amount) || 0;
      if (plan <= 0) return 0;
      const pct = (this.getFactSum(row.id) / plan) * 100;
      return Math.round(pct * 10) / 10;
    },

    getCumulativeFactSum(rowId) {
      return this.cumulativeFactSums[rowId] || 0;
    },
    getCumulativeFactPct(rowId) {
      return this.workFactPctMap[rowId] ?? 0;
    },

    collectDescendantWorks(node) {
      if (node.node_type === 'work') return [node];
      const result = [];
      for (const child of (node.children || [])) {
        result.push(...this.collectDescendantWorks(child));
      }
      return result;
    },

    getHierarchyFactPct(node) {
      const collectFloorIds = (n) => {
        if (n.node_type === 'floor') return [n.floor_map_id];
        const ids = [];
        for (const c of (n.children || [])) ids.push(...collectFloorIds(c));
        return ids;
      };
      const floorIds = node.node_type === 'floor'
        ? [node.floor_map_id]
        : collectFloorIds(node);
      if (!floorIds.length) return null;

      const prefixes = floorIds.map(id => `work_${id}_`);
      const workIds = new Set();
      for (const rowId of Object.keys(this.workPlanMap)) {
        for (const pfx of prefixes) {
          if (rowId.startsWith(pfx)) { workIds.add(rowId); break; }
        }
      }
      if (!workIds.size) return 0;

      let totalFact = 0;
      let totalPlan = 0;
      for (const wid of workIds) {
        totalFact += this.cumulativeFactSums[wid] || 0;
        totalPlan += this.workPlanMap[wid] || 0;
      }
      if (totalPlan <= 0) return 0;
      return Math.round(totalFact / totalPlan * 1000) / 10;
    },
    factDialogMaxValue() {
      const plan       = this.parseFactNum(this.factDialog.workData?.plan_total);
      const cumulative = this.parseFactNum(this.factDialog.workData?.cumulative_fact);
      return Math.max(0, this.r6(plan - cumulative));
    },
    // fmtNum, fmtKzt — provided by useFormatters via setup()
    toggleShowFullNames() {
      this.showFullNames = !this.showFullNames;
      window.localStorage.setItem('ppm_showFullNames', this.showFullNames);
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        this._prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this._prevOverflow || '';
      }
    },

    getCellMeta(rowId, colKey) {
      return this.cellMeta?.[rowId]?.[colKey] || {};
    },
    setCellMeta(rowId, colKey, meta) {
      if (!this.cellMeta[rowId]) {
        this.cellMeta = { ...this.cellMeta, [rowId]: {} };
      }
      const prev = this.cellMeta[rowId][colKey] || {};
      this.cellMeta[rowId] = { ...this.cellMeta[rowId], [colKey]: { ...prev, ...meta } };
    },
    cellClass(row, d) {
      const val   = this.getCellVal(row.id, d.key);
      const haVal = val !== '' && val !== null;
      const isDone = this.isWorkComplete(row);
      if (isDone && !haVal) return { 'cell-click--done': true };
      if (!haVal) return {};
      const meta    = this.getCellMeta(row.id, d.key);
      const factNum = parseFloat(val) || 0;
      const planNum = parseFloat(row.work_set_amount) || 0;
      const isOver  = planNum > 0 && factNum > planNum;
      return {
        'cell-click--filled': true,
        'cell-click--sent':   !!meta.isSent && !isOver,
        'cell-click--saved':  !meta.isSent  && !isOver,
        'cell-click--over':   isOver,
      };
    },

    // ──────────────────────────────────────────────
    //  Сохранение
    // ──────────────────────────────────────────────
    buildPayload() {
      const keyToDmy = {};
      this.calendarDates.forEach((d) => { keyToDmy[d.key] = this.isoToDmy(d.iso); });

      const rows = this.allWorkNodes
        .map((r) => {
          const rawValues = this.cellValues[r.id] || {};
          const values = {};
          Object.entries(rawValues).forEach(([k, v]) => {
            if (v !== null && v !== '') values[keyToDmy[k] || k] = v;
          });
          return {
            floor_map_id:            r.floor_map_id,
            usp_level_guid:          r.usp_level_guid           || null,
            usp_work_guid:           r.usp_work_guid            || null,
            work_set_id:             r.work_set_id,
            work_set_check_group_id: r.work_set_check_group_id  || null,
            name:                    r.label,
            values,
          };
        })
        .filter((r) => Object.keys(r.values).length > 0);

      return {
        project_guid:  this.projectGuid,
        project_name:  this.selectedProjectName,
        date_from:     this.dateFrom,
        date_to:       this.dateTo,
        rows,
      };
    },

    _applySentMeta(rows) {
      const isoToKey = {};
      this.calendarDates.forEach((d) => { isoToKey[d.iso] = d.key; });
      const newMeta = { ...this.cellMeta };
      rows.forEach((r) => {
        const colKey = isoToKey[r.value_date];
        if (!colKey) return;
        const rowId = `work_${r.floor_map_id}_${r.work_set_id}`;
        if (!newMeta[rowId]) newMeta[rowId] = {};
        const prev = newMeta[rowId][colKey] || {};
        newMeta[rowId] = { ...newMeta[rowId], [colKey]: { ...prev, isSent: !!r.is_sent } };
      });
      this.cellMeta = newMeta;
    },

    async loadSavedValues() {
      if (!this.projectGuid || !this.dateFrom || !this.dateTo) return;
      try {
        const operRows = await uspFactRead({
          project_guid: this.projectGuid,
          date_from:    this.dateFrom,
          date_to:      this.dateTo,
        });

        const isoToKey = {};
        this.calendarDates.forEach((d) => { isoToKey[d.iso] = d.key; });

        const newCellValues = {};
        const newCellMeta   = {};
        const operList = operRows?.data || operRows || [];
        const newCompleted = new Set();
        operList.forEach((r) => {
          const colKey = isoToKey[r.value_date];
          if (colKey) {
            const rowId = `work_${r.floor_map_id}_${r.work_set_id}`;
            if (!newCellValues[rowId]) newCellValues[rowId] = {};
            newCellValues[rowId] = { ...newCellValues[rowId], [colKey]: String(r.fact_value ?? '') };
            if (!newCellMeta[rowId]) newCellMeta[rowId] = {};
            const fp     = r.fact_price             != null ? parseFloat(r.fact_price)             : null;
            const mpt    = r.materials_price_total  != null ? parseFloat(r.materials_price_total)  : 0;
            const mSmr   = r.materials_price_smr    != null ? parseFloat(r.materials_price_smr)    : 0;
            const mTmc   = r.materials_price_tmc    != null ? parseFloat(r.materials_price_tmc)    : mpt;
            newCellMeta[rowId] = {
              ...newCellMeta[rowId],
              [colKey]: {
                isSent:               !!r.is_sent,
                hasPendingCorrection: !!r.has_pending_correction,
                factPrice:            fp,
                factPriceOrigin:      r.fact_price_origin           != null ? parseFloat(r.fact_price_origin)           : null,
                workSetPrice:         r.work_set_price              != null ? parseFloat(r.work_set_price)              : null,
                materialsPriceTotal:  mpt,
                materialsPriceSmr:    mSmr,
                materialsPriceTmc:    mTmc,
                materialsPriceOriginTotal: r.materials_price_origin_total != null ? parseFloat(r.materials_price_origin_total) : 0,
                totalPrice:           fp != null ? fp + mpt : null,
              },
            };
          }
          if (r.is_complete) {
            newCompleted.add(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}`);
          }
        });

        this.cellValues     = newCellValues;
        this.cellMeta       = newCellMeta;
        this.completedWorks = newCompleted;
      } catch (e) {
        console.error('loadSavedValues error', e);
      }
    },

    async loadCumulativeFacts() {
      if (!this.projectGuid) return;
      try {
        const res = await uspFactSummaryRead({ project_guid: this.projectGuid });
        const rows = res?.data || res || [];
        const sums = {};
        const pctMap = {};
        const planMap = {};
        for (const r of rows) {
          const rowId = `work_${r.floor_map_id}_${r.work_set_id}`;
          sums[rowId]    = (sums[rowId] || 0) + (parseFloat(r.total_fact_value) || 0);
          planMap[rowId] = (planMap[rowId] || 0) + (parseFloat(r.work_set_amount) || 0);
          const pct = parseFloat(r.fact_pct);
          if (!isNaN(pct)) pctMap[rowId] = Math.max(pctMap[rowId] || 0, pct);
        }
        this.cumulativeFactSums = sums;
        this.workFactPctMap     = pctMap;
        this.workPlanMap         = planMap;
      } catch (e) {
        console.error('loadCumulativeFacts error', e);
      }
    },

    async loadSmetaChangeLog() {
      if (!this.projectGuid) return;
      try {
        const rows = await smetaChangeLogUnsignedRead(this.projectGuid);
        const map = {};
        for (const r of (rows || [])) {
          if (r.floor_map_id != null && r.work_set_id != null) {
            const key = `${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id || 0}`;
            map[key] = {
              status_code: r.status_code || 'changed',
              old_price:   r.old_price,
              new_price:   r.new_price,
              old_amount:  r.old_amount,
              new_amount:  r.new_amount,
              old_total_sum: r.old_total_sum,
              new_total_sum: r.new_total_sum,
              rowversion:  r.rowversion,
            };
          }
        }
        this.smetaChangeLog = map;
      } catch {
        this.smetaChangeLog = {};
      }
    },

    // ──────────────────────────────────────────────
    //  Модалка истории фактов
    // ──────────────────────────────────────────────
    async openFactHistoryDialog(row) {
      const dlg = this.factHistoryDialog;
      dlg.show    = true;
      dlg.loading = true;
      dlg.title   = row.label || row.id;
      dlg.rows    = [];
      dlg.totalSmr = 0;
      dlg.totalTmc = 0;
      dlg.factPct  = 0;
      dlg.nodeType = row.node_type;
      dlg.filterType  = null;
      dlg.filterBlock = null;
      dlg.filterWork  = '';

      try {
        const floorIds = [];
        if (row.node_type === 'work' || row.node_type === 'group' || row.node_type === 'floor') {
          floorIds.push(row.floor_map_id);
        } else {
          const walk = (n) => {
            if (n.node_type === 'floor') { floorIds.push(n.floor_map_id); return; }
            (n.children || []).forEach(walk);
          };
          (row.children || []).forEach(walk);
        }

        const params = { project_guid: this.projectGuid };
        if (floorIds.length) params.floor_map_ids = floorIds;

        const res  = await uspFactHistoryRead(params);
        const list = res?.data || res || [];

        let totalSmr = 0;
        let totalTmc = 0;
        const mapped = [];
        for (const r of list) {
          const smr = parseFloat(r.fact_price) || 0;
          const tmc = parseFloat(r.materials_price_total) || 0;
          totalSmr += smr;
          totalTmc += tmc;
          mapped.push({
            projectType: r.project_type_name || '—',
            date:      r.value_date ? this.isoToDmy(String(r.value_date).slice(0, 10)) : '',
            dateIso:   r.value_date ? String(r.value_date).slice(0, 10) : '',
            block:     r.block_name || '',
            floor:     r.level_num != null ? `Этаж ${r.level_num}` : '',
            groupName: r.group_name || '—',
            workName:  r.work_set_name || '—',
            workGuid:  r.usp_work_guid || '',
            factValue: parseFloat(r.fact_value) || 0,
            smr,
            tmc,
            isSent:    !!r.is_sent,
            uspFactId:            r.usp_fact_id ?? null,
            floorMapId:           r.floor_map_id ?? null,
            workSetId:            r.work_set_id ?? null,
            workSetCheckGroupId:  r.work_set_check_group_id ?? null,
            workSetAmount:        parseFloat(r.work_set_amount) || 0,
            cumulativeFact:       parseFloat(r.cumulative_fact) || 0,
            originalFactValue:    String(parseFloat(r.fact_value) || 0),
            newFactValue:         String(parseFloat(r.fact_value) || 0),
            stornoDiff:           0,
            saving:               false,

            materialsOpen:    false,
            materialsLoaded:  false,
            materialsLoading: false,
            workMaterials:    [],
            originalMaterials: [],
          });
        }

        dlg.rows     = mapped;
        dlg.totalSmr = totalSmr;
        dlg.totalTmc = totalTmc;
        dlg.factPct  = this.getCumulativeFactPct(row.id) || this.hierarchyPctMap[row.id] || 0;
      } catch (e) {
        console.error('openFactHistoryDialog error', e);
      } finally {
        dlg.loading = false;
      }
    },

    async loadHistoryMaterials(row) {
      if (row.materialsLoaded || row.materialsLoading || !row.uspFactId) return;
      row.materialsLoading = true;
      try {
        const [workRes, savedMats] = await Promise.all([
          getWorkDetail(row.floorMapId, row.workSetId, row.workSetCheckGroupId),
          getUspFactMaterialByFact(row.uspFactId),
        ]);
        const allMats = workRes?.materials || [];
        const saved   = savedMats || [];

        const workMats = allMats
          .filter((m) => m.is_show !== false)
          .map((m) => {
            const sv = saved.find((s) => Number(s.material_id) === Number(m.material_id));
            const value = sv ? Number(sv.material_value ?? 0) : 0;
            return {
              material_id: m.material_id,
              name:        sv?.material_name || m.material_name || '',
              unit:        m.material_unit_name || '',
              value,
              plan_amount: parseFloat(m.material_amount) || 0,
              norm:        sv?.consumption_rate ?? m.consumption_rate ?? 0,
              remainder:   null,
            };
          });

        row.workMaterials     = workMats;
        row.originalMaterials = buildOriginalMaterialsFromWork(workMats);
        row.materialsLoaded   = true;
      } catch (e) {
        console.error('loadHistoryMaterials error', e);
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка загрузки материалов факта' });
      } finally {
        row.materialsLoading = false;
      }
    },

    onHistoryMaterialInput({ row, mat, val }) {
      applyMaterialInput({
        val,
        mat,
        originalMaterials: row.originalMaterials,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
        notify:            this.$q?.notify,
      });
    },

    async submitHistoryStornoRow(row) {
      if (!row.uspFactId) {
        this.$q?.notify?.({ type: 'negative', message: STORNO_MESSAGES.NO_RECORD_IN_DB });
        return;
      }
      const origNum = this.parseFactNum(row.originalFactValue);
      let newNum    = this._clampNewFactByPlan(row.newFactValue, row.workSetAmount, row.cumulativeFact);
      if (newNum < 0) newNum = 0;
      const diff    = this.r6(newNum - origNum);

      const triplet = buildMaterialsJsonTriplet({
        originalMaterials: row.originalMaterials,
        workMaterials:     row.workMaterials,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
      });
      const matsChanged = hasMaterialsDiff(triplet.diffMaterialsJson);

      if (diff === 0 && !matsChanged) {
        this.$q?.notify?.({ type: 'warning', message: STORNO_MESSAGES.NO_CHANGES });
        return;
      }

      row.saving = true;
      try {
        if (!row.isSent) {
          const rowRef = {
            id:              `work_${row.floorMapId}_${row.workSetId}`,
            floor_map_id:    row.floorMapId,
            work_set_id:     row.workSetId,
            work_set_check_group_id: row.workSetCheckGroupId,
            usp_work_guid:   row.workGuid || null,
          };
          const dateRef = { iso: row.dateIso, key: this._isoToColKey(row.dateIso) };
          const workDataRef = {
            plan_total:      row.workSetAmount,
            cumulative_fact: row.cumulativeFact,
            assign_id:       null,
            materials: (row.workMaterials || []).map((m) => ({
              material_id:      m.material_id,
              name:             m.name,
              value:            String(m.value ?? 0),
              isManual:         false,
              norm:             0,
              purchaseTypeId:   null,
              replacedMaterialId: null,
            })),
          };
          await this._applyStornoUnsent({
            row:               rowRef,
            date:              dateRef,
            uspFactId:         row.uspFactId,
            newNum,
            workData:          workDataRef,
            originalMaterials: row.originalMaterials,
          });
          row.stornoDiff       = diff;
          row.originalFactValue = String(newNum);
          row.factValue        = newNum;
          this.$q?.notify?.({
            type:    'positive',
            message: newNum === 0 ? 'Факт удалён (не был отправлен в УСП)' : 'Факт обновлён',
          });
          return;
        }

        await uspFactCorrectionSave({
          usp_fact_id:             row.uspFactId,
          project_guid:            this.projectGuid,
          floor_map_id:            row.floorMapId,
          work_set_id:             row.workSetId,
          work_set_check_group_id: row.workSetCheckGroupId,
          value_date:              row.dateIso,
          usp_work_guid:           row.workGuid || null,
          original_fact_value:     origNum,
          new_fact_value:          newNum,
          diff_fact_value:         diff,
          original_materials_json: matsChanged ? triplet.originalMaterialsJson : null,
          new_materials_json:      matsChanged ? triplet.newMaterialsJson      : null,
          diff_materials_json:     matsChanged ? triplet.diffMaterialsJson     : null,
        });

        row.stornoDiff = diff;

        const rowId  = `work_${row.floorMapId}_${row.workSetId}`;
        const colKey = this._isoToColKey(row.dateIso);
        if (colKey) {
          this.setCellMeta(rowId, colKey, { hasPendingCorrection: true });
        }

        this._notifyCorrectionSaved(diff, newNum);
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: STORNO_MESSAGES.SAVE_ERROR });
      } finally {
        row.saving = false;
      }
    },

    /** Ограничить newFactValue остатком (plan - cumulative), если exceed запрещён */
    _clampNewFactByPlan(newFactValue, planTotal, cumulativeFact) {
      let newNum = this.parseFactNum(newFactValue);
      if (this.isFactExceedAllowed) return newNum;
      const plan       = this.parseFactNum(planTotal);
      const cumulative = this.parseFactNum(cumulativeFact);
      if (plan > 0) {
        const maxVal = Math.max(0, this.r6(plan - cumulative));
        if (newNum > maxVal) newNum = maxVal;
      }
      return newNum;
    },

    _isoToColKey(iso) {
      const d = (this.calendarDates || []).find((x) => x.iso === iso);
      return d?.key || null;
    },

    _notifyCorrectionSaved(diff, newNum) {
      const label = newNum === 0
        ? STORNO_MESSAGES.FULL_STORNO
        : `Корректировка зарегистрирована (${diff >= 0 ? '+' : ''}${this.round6(diff)})`;
      this.$q?.notify?.({
        type:    'positive',
        message: `${label}. ${STORNO_MESSAGES.WILL_APPLY_ON_SEND}`,
        timeout: 5000,
      });
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$q.notify({ message: 'Скопировано', color: 'positive', timeout: 1000, position: 'top' });
      }).catch(() => {
        this.$q.notify({ message: 'Не удалось скопировать', color: 'negative', timeout: 1500, position: 'top' });
      });
    },

    // ──────────────────────────────────────────────
    //  Discrepancy report (Расхождения с УСП)
    // ──────────────────────────────────────────────
    async openCronHistory(logType) {
      const dlgRef = this.$refs.discrepancyDialogRef;
      if (!dlgRef) return;
      dlgRef.historyDialog.logType = logType;
      dlgRef.historyDialog.show = true;
      dlgRef.historyDialog.loading = true;
      dlgRef.historyDialog.rows = [];
      const rows = await cronHistoryRead(this.projectGuid, logType);
      dlgRef.historyDialog.rows = rows;
      dlgRef.historyDialog.loading = false;
    },

    async openCronLogJson(logId, logType) {
      const dlgRef = this.$refs.discrepancyDialogRef;
      if (!dlgRef) return;
      dlgRef.jsonViewDialog.show = true;
      dlgRef.jsonViewDialog.loading = true;
      dlgRef.jsonViewDialog.content = '';
      try {
        const data = await cronLogJsonRead(logId, logType);
        dlgRef.jsonViewDialog.content = JSON.stringify(data, null, 2);
      } catch (e) {
        dlgRef.jsonViewDialog.content = 'Ошибка загрузки: ' + (e?.message || '');
      } finally {
        dlgRef.jsonViewDialog.loading = false;
      }
    },

    async loadCronStatus() {
      const dlgRef = this.$refs.discrepancyDialogRef;
      if (!dlgRef) return;
      const row = await cronStatusRead(this.projectGuid);
      if (!row) return;
      if (row.estimate_send_datetime) {
        const dt = new Date(row.estimate_send_datetime);
        dlgRef.lastRun.smeta = {
          ok: row.estimate_status === 'SUCCESS',
          time: dt.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
          message: row.estimate_error || '',
        };
      }
      if (row.contract_send_datetime) {
        const dt = new Date(row.contract_send_datetime);
        dlgRef.lastRun.contracts = {
          ok: row.contract_status === 'SUCCESS',
          time: dt.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
          message: row.contract_error || '',
        };
      }
    },

    async openStornoListDialog() {
      if (!this.hasStornoListGrant) {
        this.$q.notify({ type: 'negative', message: 'Нет прав на сторнирование (M__ProjectPatternStornoList)' });
        return;
      }
      const dlg = this.stornoListDialog;
      dlg.show = true;
      dlg.loading = true;
      dlg.rows = [];
      dlg.factItems = [];
      dlg.presetFilterType = this.filterType
        ? (this.projectTypeOptions.find(t => t.id === this.filterType)?.name || null)
        : null;
      try {
        const [discRows, factRows] = await Promise.all([
          uspDiscrepancyRead(this.projectGuid),
          uspStornoListRead(this.projectGuid),
        ]);
        dlg.rows = discRows || [];
        dlg.factItems = factRows || [];
      } catch (e) {
        this.$q.notify({ message: 'Ошибка загрузки списка сторно', color: 'negative', position: 'top' });
      } finally {
        dlg.loading = false;
      }
    },

    async reloadStornoList() {
      const dlg = this.stornoListDialog;
      dlg.loading = true;
      dlg.rows = [];
      dlg.factItems = [];
      try {
        const [discRows, factRows] = await Promise.all([
          uspDiscrepancyRead(this.projectGuid),
          uspStornoListRead(this.projectGuid),
        ]);
        dlg.rows = discRows || [];
        dlg.factItems = factRows || [];
      } catch (e) {
        this.$q.notify({ message: 'Ошибка обновления списка сторно', color: 'negative', position: 'top' });
      } finally {
        dlg.loading = false;
      }
    },

    async stornoFromList(items) {
      const list = (items || []).filter(it => it && it.usp_fact_id);
      if (!list.length) {
        this.$q.notify({ type: 'warning', message: 'Нет выбранных записей для сторнирования' });
        return;
      }

      const sentItems     = list.filter(it => it.sent_at);
      const unsentItems   = list.filter(it => !it.sent_at);
      const sentFactIds   = sentItems.map(it => it.usp_fact_id);
      const unsentFactIds = unsentItems.map(it => it.usp_fact_id);

      if (!sentFactIds.length) {
        this.$q.dialog({
          title: 'Удалить записи?',
          message: `Выбрано ${unsentFactIds.length} неотправленных записей. Они будут удалены из БД без отправки в УСП.`,
          cancel: true,
          ok: { label: 'Удалить', color: 'negative' },
        }).onOk(async () => {
          try {
            await Promise.all(unsentFactIds.map(id => uspFactDelete({ usp_fact_id: id })));
            this.$q.notify({ type: 'positive', message: `Удалено ${unsentFactIds.length} записей`, timeout: 3000, position: 'top' });
            await this.reloadStornoList();
          } catch (e) {
            this.$q.notify({ type: 'negative', message: 'Ошибка удаления: ' + (e?.message || ''), position: 'top' });
          }
        });
        return;
      }

      let content = '{}';
      const dismiss = this.$q.notify({
        type:    'ongoing',
        message: `Построение сторно-JSON для ${sentFactIds.length} записей…`,
        timeout: 0,
        spinner: true,
      });
      try {
        const res = await uspFactBuildStornoJsonPartial({
          project_guid: this.projectGuid,
          fact_ids:     sentFactIds,
        });
        dismiss();
        content = JSON.stringify(res, null, 2);
      } catch (e) {
        dismiss();
        this.$q.notify({ type: 'negative', message: e?.message || 'Ошибка построения сторно-JSON' });
        return;
      }

      this.jsonDialog.content        = content;
      this.jsonDialog.actionType     = ACTION_TYPE.STORNO_PARTIAL;
      this.jsonDialog.workGuids      = [...new Set(list.map(it => it.work_guid).filter(Boolean))];
      this.jsonDialog.factIds        = sentFactIds;
      this.jsonDialog.unsentFactIds  = unsentFactIds;
      this.jsonDialog.show           = true;
    },

    async openDiscrepancyDialog() {
      const dlg = this.discrepancyDialog;
      dlg.show = true;
      dlg.loading = true;
      dlg.rows = [];
      dlg.search = '';
      dlg.filterType = null;
      dlg.filterBlock = null;
      dlg.filterFloor = null;
      dlg.filterSource = 'all';
      try {
        const [rows] = await Promise.all([
          uspDiscrepancyRead(this.projectGuid),
          this.$nextTick().then(() => this.loadCronStatus()),
        ]);
        dlg.rows = rows || [];
      } catch (e) {
        this.$q.notify({ message: 'Ошибка загрузки расхождений', color: 'negative', position: 'top' });
      } finally {
        dlg.loading = false;
      }
    },

    async refreshDiscrepancyData() {
      const dlg = this.discrepancyDialog;
      dlg.refreshing = true;
      try {
        const res = await uspSbsDataRefresh(this.projectGuid);
        this.$q.notify({
          type: res?.success ? 'positive' : 'warning',
          message: res?.message || 'Готово',
          timeout: 4000,
        });
        if (res?.success) {
          const rows = await uspDiscrepancyRead(this.projectGuid);
          dlg.rows = rows || [];
        }
      } catch (e) {
        this.$q.notify({ type: 'negative', message: 'Ошибка обновления: ' + (e?.message || '') });
      } finally {
        dlg.refreshing = false;
      }
    },

    async runCronUpdateSmeta() {
      const dlgRef = this.$refs.discrepancyDialogRef;
      if (dlgRef) dlgRef.cronLoading.smeta = true;
      try {
        const rows = await cronUpdateSmeta();
        const errors = (rows || []).filter(r => r.error);
        const ok = errors.length === 0;
        this.$q.notify({ type: ok ? 'positive' : 'warning', message: ok ? 'Смета отправлена' : 'Часть проектов с ошибками', timeout: 4000 });
        await this.loadCronStatus();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || '';
        this.$q.notify({ type: 'negative', message: 'Ошибка отправки сметы: ' + msg });
        await this.loadCronStatus();
      } finally {
        if (dlgRef) dlgRef.cronLoading.smeta = false;
      }
    },

    async runCronCreateContracts() {
      const dlgRef = this.$refs.discrepancyDialogRef;
      if (dlgRef) dlgRef.cronLoading.contracts = true;
      try {
        const rows = await cronCreateContracts();
        const errors = (rows || []).filter(r => r.error);
        const ok = errors.length === 0;
        this.$q.notify({ type: ok ? 'positive' : 'warning', message: ok ? 'Договора отправлены' : 'Часть проектов с ошибками', timeout: 4000 });
        await this.loadCronStatus();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || '';
        this.$q.notify({ type: 'negative', message: 'Ошибка отправки договоров: ' + msg });
        await this.loadCronStatus();
      } finally {
        if (dlgRef) dlgRef.cronLoading.contracts = false;
      }
    },

    async alignFactToSmeta(row) {
      const combos = row.combos || [];
      if (!combos.length) {
        this.$q.notify({ type: 'warning', message: 'Нет связок для выравнивания' });
        return;
      }
      this.alignDialog.rows = [row];
      this.alignDialog.combos = combos;
      this.alignDialog.title = row.work_set_name || row.work_guid;
      await this.openAlignDialog();
    },

    async alignFactToSmetaBulk(rows) {
      const allCombos = [];
      for (const r of rows) {
        for (const c of (r.combos || [])) allCombos.push(c);
      }
      if (!allCombos.length) {
        this.$q.notify({ type: 'warning', message: 'Нет данных для выравнивания' });
        return;
      }
      this.alignDialog.rows = rows;
      this.alignDialog.combos = allCombos;
      this.alignDialog.title = `Массовое выравнивание (${rows.length} работ, ${allCombos.length} связок)`;
      await this.openAlignDialog();
    },

    async openAlignDialog() {
      this.alignDialog.summary = null;
      this.alignDialog.error = '';
      this.alignDialog.show = true;
      await this.runAlignDryRun();
    },

    async runAlignDryRun() {
      this.alignDialog.busy = true;
      this.alignDialog.error = '';
      try {
        const result = await uspFactAlignToSmeta(
          this.projectGuid,
          this.alignDialog.combos,
          { force: false, dryRun: true }
        );
        this.alignDialog.summary = result || { combos_aligned: 0, combos_skipped: 0, plan: [] };
      } catch (e) {
        this.alignDialog.error = 'Ошибка при проверке: ' + (e?.message || '');
      } finally {
        this.alignDialog.busy = false;
      }
    },

    async recheckAlignToSmeta() {
      await this.runAlignDryRun();
    },

    async applyAlignToSmeta({ force }) {
      const summary = this.alignDialog.summary || {};
      const total = (summary.combos_aligned || 0) + (summary.combos_skipped || 0);
      const willAlign = force ? total : (summary.combos_aligned || 0);
      const willSkip  = force ? 0 : (summary.combos_skipped || 0);
      const willOrphan = summary.combos_orphaned || 0;
      if (!willAlign && !willOrphan) return;

      const modeLabel = force ? 'принудительный режим' : 'безопасный режим';
      const warning = force
        ? '<div style="margin-top:8px;color:#c62828">Записи будут созданы даже там, где оперфакта не вводили.</div>'
        : (willSkip > 0
            ? `<div style="margin-top:8px;color:#ef6c00">${willSkip} связок без оперфакта будут пропущены.</div>`
            : '');
      const orphanWarning = willOrphan > 0
        ? `<div style="margin-top:8px;color:#c62828">Дополнительно будет <b>удалено ${willOrphan}</b> фантомных записей факта (нет в смете).</div>`
        : '';

      this.$q.dialog({
        title: 'Подтверждение',
        html: true,
        message: `
          <div style="font-size:13px;line-height:1.6">
            Применить выравнивание <b>${willAlign}</b> связок (${modeLabel})?
            <div style="margin-top:6px;color:#e65100">Старые записи факта по этим связкам будут удалены и пересозданы из актуальной сметы.</div>
            ${warning}
            ${orphanWarning}
          </div>`,
        ok: { label: 'Применить', color: force ? 'red-7' : 'green-7', unelevated: true, noCaps: true },
        cancel: { label: 'Отмена', flat: true, noCaps: true },
        persistent: true,
      }).onOk(async () => {
        this.alignDialog.applying = true;
        this.alignDialog.error = '';
        try {
          const result = await uspFactAlignToSmeta(
            this.projectGuid,
            this.alignDialog.combos,
            { force: !!force, dryRun: false }
          );
          const aligned = result?.combos_aligned ?? 0;
          const skipped = result?.combos_skipped ?? 0;
          const orphaned = result?.combos_orphaned ?? 0;
          const parts = [`выровнено ${aligned}`];
          if (skipped) parts.push(`пропущено ${skipped}`);
          if (orphaned) parts.push(`удалено фантомов ${orphaned}`);
          this.$q.notify({
            type: 'positive',
            message: `${parts.join(', ')} (${modeLabel})`,
            timeout: 5000,
          });
          this.alignDialog.show = false;
          const freshRows = await uspDiscrepancyRead(this.projectGuid);
          this.discrepancyDialog.rows = freshRows || [];
        } catch (e) {
          this.alignDialog.error = 'Ошибка выравнивания: ' + (e?.message || '');
          this.$q.notify({
            type: 'negative',
            message: this.alignDialog.error,
            position: 'top',
          });
        } finally {
          this.alignDialog.applying = false;
        }
      });
    },

    async alignToUspBulk(rows, opts = {}) {
      const perPeriod = opts.perPeriod !== false;
      await this._runBulkJsonBuild({
        rows,
        actionType:      ACTION_TYPE.CORRECTION,
        buildFn:         uspFactBuildCorrectionJson,
        emptyMessage:    'Нет work_guid для отправки',
        progressLabel:   'Построение JSON',
        errorMessage:    'Ошибка построения JSON',
        extraBody:       { per_period: perPeriod },
        perPeriod,
      });
    },

    async stornoBulk(rows) {
      await this._runBulkJsonBuild({
        rows,
        actionType:      ACTION_TYPE.STORNO,
        buildFn:         uspFactBuildStornoJson,
        emptyMessage:    'Нет work_guid для сторнирования',
        progressLabel:   'Построение сторно-JSON',
        errorMessage:    'Ошибка построения сторно-JSON',
      });
    },

    async sendSmetaBulk(rows) {
      await this._runBulkJsonBuild({
        rows,
        actionType:      ACTION_TYPE.SEND_SMETA,
        buildFn:         uspEstimateBuildJson,
        emptyMessage:    'Нет work_guid для отправки сметы',
        progressLabel:   'Построение JSON сметы',
        errorMessage:    'Ошибка построения JSON сметы',
      });
    },

    /** Общий конвейер: собрать work_guid, дёрнуть buildFn и показать jsonDialog */
    async _runBulkJsonBuild({ rows, actionType, buildFn, emptyMessage, progressLabel, errorMessage, extraBody = {}, perPeriod = true }) {
      const workGuids = (rows || []).map((r) => r.work_guid).filter(Boolean);
      if (!workGuids.length) {
        this.$q.notify({ type: 'warning', message: emptyMessage });
        return;
      }
      const dismiss = this.$q.notify({
        type:    'ongoing',
        message: `${progressLabel} для ${workGuids.length} работ…`,
        timeout: 0,
        spinner: true,
      });
      try {
        const res = await buildFn({
          project_guid: this.projectGuid,
          work_guids:   workGuids,
          ...extraBody,
        });
        dismiss();
        this.jsonDialog.content    = JSON.stringify(res, null, 2);
        this.jsonDialog.actionType = actionType;
        this.jsonDialog.workGuids  = workGuids;
        this.jsonDialog.perPeriod  = perPeriod;
        this.jsonDialog.show       = true;
      } catch (e) {
        dismiss();
        this.$q.notify({ type: 'negative', message: e?.message || errorMessage });
      }
    },

    async sendJsonAction() {
      const { actionType, workGuids, factIds, unsentFactIds = [] } = this.jsonDialog;
      const hasFactIds   = Array.isArray(factIds) && factIds.length > 0;
      const hasUnsent    = Array.isArray(unsentFactIds) && unsentFactIds.length > 0;
      if (!actionType) return;
      if (actionType === ACTION_TYPE.STORNO_PARTIAL ? (!hasFactIds && !hasUnsent) : !workGuids.length) return;

      this.jsonDialog.sending = true;
      try {
        let res;
        if (actionType === ACTION_TYPE.SEND_SMETA) {
          const raw = await uspEstimateSendSelected({
            project_guid: this.projectGuid,
            work_guids:   workGuids,
          });
          res = raw?.data ?? raw ?? {};
        } else if (actionType === ACTION_TYPE.STORNO_PARTIAL) {
          if (hasUnsent) {
            await Promise.all(unsentFactIds.map(id => uspFactDelete({ usp_fact_id: id })));
          }
          if (hasFactIds) {
            res = await uspFactSendActionPartial({
              project_guid: this.projectGuid,
              fact_ids:     factIds,
            });
          } else {
            res = { Success: true, Message: `Удалено ${unsentFactIds.length} неотправленных записей` };
          }
        } else {
          res = await uspFactSendAction({
            project_guid: this.projectGuid,
            work_guids:   workGuids,
            action_type:  actionType,
            per_period:   actionType === ACTION_TYPE.CORRECTION
              ? this.jsonDialog.perPeriod !== false
              : actionType === ACTION_TYPE.STORNO
                ? true
                : undefined,
          });
        }
        this.jsonDialog.sending = false;

        if (res?.Success) {
          this.jsonDialog.show = false;
          this.$q.notify({
            type: 'positive',
            message: `${ACTION_LABEL[actionType] || actionType} отправлена. ${res.Message || ''}`,
            timeout: 5000,
          });
          if (actionType === ACTION_TYPE.STORNO_PARTIAL && this.stornoListDialog.show) {
            await this.reloadStornoList();
          } else {
            const freshRows = await uspDiscrepancyRead(this.projectGuid);
            this.discrepancyDialog.rows = freshRows || [];
          }
        } else {
          this.$q.notify({
            type: 'negative',
            message: res?.Message || 'Ошибка отправки',
            timeout: 5000,
          });
        }
      } catch (e) {
        this.jsonDialog.sending = false;
        this.$q.notify({ type: 'negative', message: e?.message || 'Ошибка отправки в УСП' });
      }
    },

    async openDiscrepancyDetail(row) {
      const dlg = this.discrepancyDetailDialog;
      dlg.show = true;
      dlg.loading = true;
      dlg.workGuid = row.work_guid;
      dlg.workName = row.work_set_name || row.work_guid;
      dlg.rows = [];
      try {
        const rows = await uspDiscrepancyDetailRead(this.projectGuid, row.work_guid);
        dlg.rows = rows || [];
      } catch (e) {
        this.$q.notify({ message: 'Ошибка загрузки детализации', color: 'negative', position: 'top' });
      } finally {
        dlg.loading = false;
      }
    },

    // fmtDisc, fmtDiscDelta, discDeltaClass, discRowClass, discPeriodLabel, discFmtDate, discFmtDateTime — provided by useFormatters via setup()

    // ──────────────────────────────────────────────
    //  JSON
    // ──────────────────────────────────────────────
    // isoToDmy — provided by useFormatters via setup()
    openJson() {
      const payload = this.buildPayload();
      this.jsonDialog.content = JSON.stringify(payload, null, 2);
      this.jsonDialog.actionType = '';
      this.jsonDialog.workGuids = [];
      this.jsonDialog.show    = true;
    },

    // ──────────────────────────────────────────────
    //  УСП JSON
    // ──────────────────────────────────────────────
    async openUspDialog() {
      this.uspDialog.jsonContent    = '';
      this.uspDialog.stornoSelected = [];
      this.uspDialog.workGroups     = [];
      this.uspDialog.savedRows      = [];
      this.uspDialog.show           = true;

      if (!this.projectGuid || !this.dateFrom || !this.dateTo) return;

      this.uspDialog.loadingData = true;
      try {
        const rows = await uspFactRead({
          project_guid: this.projectGuid,
          date_from:    this.dateFrom,
          date_to:      this.dateTo,
        });
        this.uspDialog.savedRows = rows || [];

        // Обогащаем work_name из дерева по work_set_id
        const workNameMap = {};
        this.allWorkNodes.forEach((n) => {
          if (n.work_set_id) workNameMap[n.work_set_id] = n.label;
        });
        const augmented = (rows || []).map((r) => ({
          ...r,
          work_name: workNameMap[r.work_set_id] || String(r.usp_work_guid || '').slice(0, 8) || '—',
        }));

        this.uspDialog.workGroups = buildUspWorkGroups(augmented);

        // Предвыбор сторно: работы которые уже отправлены или имеют несколько дат
        this.uspDialog.stornoSelected = this.uspDialog.workGroups
          .filter((g) => g.is_sent || g.dateCount > 1)
          .map((g) => g.usp_work_guid);
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: e?.message || 'Ошибка загрузки данных' });
      } finally {
        this.uspDialog.loadingData = false;
      }
    },

    // uspSelectAll — moved to PpmUspDialog

    async sendToUsp() {
      this.uspSending = true;
      try {
        const res = await uspFactSend({
          project_guid: this.projectGuid,
          date_from:    this.dateFrom,
          date_to:      this.dateTo,
        });

        const success         = res?.Success === true;
        const message         = res?.Message || '';
        const sentCount       = res?.SentCount ?? 0;
        const correctionCount = res?.CorrectionApplyCount ?? 0;
        const correctionMsg   = res?.CorrectionMessage || '';

        if (success) {
          const parts = [`Отправлено в УСП: ${sentCount} записей`];
          if (correctionCount > 0) {
            parts.push(`корректировок применено: ${correctionCount}`);
          }
          this.$q?.notify?.({
            type:    'positive',
            message: parts.join(' · '),
            caption: correctionMsg || undefined,
            timeout: 4000,
          });
          await this.loadSavedValues();
        } else {
          this.$q?.notify?.({
            type:    'negative',
            message: message || 'Ошибка отправки в УСП',
            timeout: 6000,
          });
        }
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: e?.message || 'Ошибка отправки в УСП' });
      } finally {
        this.uspSending = false;
      }
    },

    async doBuildUspJson() {
      this.uspDialog.loading = true;
      try {
        const workGuids = [...new Set(
          (this.uspDialog.savedRows || [])
            .map(r => r.usp_work_guid)
            .filter(Boolean)
        )];

        if (!workGuids.length) {
          this.$q?.notify?.({ type: 'warning', message: 'Нет работ с usp_work_guid' });
          return;
        }

        const res = await uspFactBuildJsonSelected({
          project_guid: this.projectGuid,
          work_guids: workGuids,
        });

        this.uspDialog.jsonContent = JSON.stringify(res, null, 2);
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: e?.message || 'Ошибка построения JSON' });
      } finally {
        this.uspDialog.loading = false;
      }
    },

    async copyUspJson() {
      try {
        await navigator.clipboard.writeText(this.uspDialog.jsonContent || '');
        this.$q?.notify?.({ type: 'positive', message: 'Скопировано' });
      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Не удалось скопировать' });
      }
    },

    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text || '');
        this.$q?.notify?.({ type: 'positive', message: 'Скопировано' });
      } catch { /* ignore */ }
    },

    // fmtDateRu, fmtDateTimeRu — provided by useFormatters via setup()

    // uspReportToggleSort, uspReportResetFilters — moved to PpmUspReportDialog

    // ── Отчёт УСП ──
    async openUspReport() {
      this.uspReportDialog.show = true;
      this.uspReportDialog.loading = true;
      this.uspReportDialog.sort = { col: null, dir: 'asc' };
      this.uspReportDialog.search = '';
      this.uspReportDialog.dateFrom = '';
      this.uspReportDialog.dateTo = '';
      this.uspReportDialog.filterType = null;
      this.uspReportDialog.filterBlock = null;
      this.uspReportDialog.filterFloor = null;
      try {
        const rows = await uspReportRead(this.projectGuid);
        this.uspReportDialog.rows = rows || [];
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка загрузки отчёта: ' + (e?.message || '') });
        this.uspReportDialog.rows = [];
      } finally {
        this.uspReportDialog.loading = false;
      }
    },

    async refreshUspSbsData() {
      this.uspReportDialog.refreshing = true;
      try {
        const res = await uspSbsDataRefresh(this.projectGuid);
        this.$q?.notify?.({
          type: res?.success ? 'positive' : 'warning',
          message: res?.message || 'Готово',
          timeout: 4000,
        });
        if (res?.success) {
          const rows = await uspReportRead(this.projectGuid);
          this.uspReportDialog.rows = rows || [];
        }
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка обновления: ' + (e?.message || '') });
      } finally {
        this.uspReportDialog.refreshing = false;
      }
    },

    async openUspWorkDetail(row) {
      this.uspWorkDetailDialog.show    = true;
      this.uspWorkDetailDialog.loading = true;
      this.uspWorkDetailDialog.workName = (row.work_sbs_id ? row.work_sbs_id + ' / ' : '') + (row.work_name || '');
      this.uspWorkDetailDialog.workGuid = row.usp_work_guid || '';
      this.uspWorkDetailDialog.facts     = [];
      this.uspWorkDetailDialog.materials = [];
      try {
        const data = await uspReportWorkDetailRead(
          this.projectGuid, row.floor_map_id, row.work_set_id, row.work_set_check_group_id
        );
        const all = data || [];
        this.uspWorkDetailDialog.facts     = all.filter(r => r.row_type === 'fact');
        this.uspWorkDetailDialog.materials = all.filter(r => r.row_type === 'material');
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка: ' + (e?.message || '') });
      } finally {
        this.uspWorkDetailDialog.loading = false;
      }
    },

    async openUspGuidDetail(uspWorkGuid) {
      this.uspGuidDetailDialog.show    = true;
      this.uspGuidDetailDialog.loading = true;
      this.uspGuidDetailDialog.uspGuid = uspWorkGuid;
      this.uspGuidDetailDialog.rows    = [];
      try {
        const data = await uspReportUspDetailRead(this.projectGuid, uspWorkGuid);
        this.uspGuidDetailDialog.rows = data || [];
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка: ' + (e?.message || '') });
      } finally {
        this.uspGuidDetailDialog.loading = false;
      }
    },


    // ──────────────────────────────────────────────
    //  Диалог: Заполнение Оперфакта
    // ──────────────────────────────────────────────
    async openFactDialog(row, date) {
      const smetaKey = `${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id || 0}`;
      if (this.smetaChangeLog[smetaKey]) {
        this.smetaChangeDialog = {
          show: true,
          workName: row.label || row.fullLabel || '',
          info: this.smetaChangeLog[smetaKey],
        };
        return;
      }
      const loadId = ++this._ppmFactDlgLoadId;
      const saved = this.getCellVal(row.id, date.key);
      const meta = this.getCellMeta(row.id, date.key);
      const hasFactValue = saved !== null && saved !== '' && this.parseFactNum(saved) !== 0;
      const isStorno = this.stornoMode && (!!meta.isSent || hasFactValue);
      this.factDialog = {
        show:          true,
        row,
        date,
        loading:       true,
        factValue:     saved !== null && saved !== '' ? String(saved) : '',
        factPct:       0,
        materialsOpen: false,
        matSyncFact:   false,
        saving:          false,
        deleting:        false,
        workData:        null,
        isSent:          !!meta.isSent,
        minFactValue:    0,
        uspFactId:       null,
        factPrice:       null,
        factPriceOrigin: null,
        materialsPrice:  0,
        isStornoMode:    isStorno,
        ...createStornoFields(saved),
        correctionHistory:        [],
        correctionHistoryLoading: false,
      };
      try {
        // Загружаем описание работы и материалы по норме
        const [res, factRes] = await Promise.all([
          getWorkDetail(row.floor_map_id, row.work_set_id, row.work_set_check_group_id),
          uspFactRead({
            project_guid:             this.projectGuid,
            floor_map_id:             row.floor_map_id,
            work_set_id:              row.work_set_id,
            work_set_check_group_id:  row.work_set_check_group_id,
            value_date:               date.iso,
          }),
        ]);

        if (loadId !== this._ppmFactDlgLoadId) return;

        const work         = res?.work      || null;
        const normMaterials = res?.materials || [];
        // Ранее сохранённый факт за эту дату (если есть) — только совпадение value_date
        const savedFact    = this.pickUspFactForDate(this.uspFactRowsFromResponse(factRes), date.iso);
        const savedMats    = savedFact?.materials || [];

        if (savedFact) {
          // Если есть сохранённый факт — берём значение и статус оттуда
          this.factDialog.factValue       = String(savedFact.fact_value ?? '');
          this.factDialog.minFactValue    = this.parseFactNum(savedFact.fact_value ?? 0);
          this.factDialog.uspFactId       = savedFact.usp_fact_id ?? null;
          this.factDialog.factPrice       = savedFact.fact_price        != null ? parseFloat(savedFact.fact_price)        : null;
          this.factDialog.factPriceOrigin = savedFact.fact_price_origin != null ? parseFloat(savedFact.fact_price_origin) : null;
          // Сумма материалов из уже загруженного массива materials
          this.factDialog.materialsPrice  = (savedFact.materials || []).reduce(
            (s, m) => s + (parseFloat(m.material_fact_price) || 0), 0
          );
          // Синхронизируем cellMeta
          const fp  = this.factDialog.factPrice;
          const mpt = this.factDialog.materialsPrice;
          this.setCellMeta(row.id, date.key, {
            isSent:              !!savedFact.is_sent,
            factPrice:           fp,
            factPriceOrigin:     this.factDialog.factPriceOrigin,
            materialsPriceTotal: mpt,
            totalPrice:          fp != null ? fp + mpt : null,
          });

          if (this.stornoMode && savedFact.usp_fact_id) {
            this.factDialog.isStornoMode = true;
            Object.assign(this.factDialog, createStornoFields(savedFact.fact_value));
          }
        }

        // cumulative_fact — факт за все другие дни, кроме выбранного.
        // Приходит из БД (поле cumulative_fact в usp_fact__read).
        // Если записи за дату ещё нет — весь use_amount считается «предыдущим».
        const planTotal_     = work?.work_set_amount ?? 0;
        if (work?.work_set_amount != null) {
          row.work_set_amount = this.r6(work.work_set_amount);
        }
        const cumulativeFact = savedFact
          ? (savedFact.cumulative_fact ?? 0)
          : (work?.use_amount ?? 0);
        const factForDate    = this.parseFactNum(this.factDialog.factValue);

        this.factDialog.workData = work ? {
          work_set_id:      work.work_set_id,
          work_set_name:    work.work_set_name,
          unit:             work.work_set_unit_name || '',
          plan_total:       planTotal_,
          fact_total:       work.use_amount ?? 0,
          fact_pct:         work.work_set_amount
            ? Math.round((work.use_amount / work.work_set_amount) * 100)
            : 0,
          fact_executor:    work.use_amount ?? 0,
          cumulative_fact:  cumulativeFact,
          remainder:        Math.max(0, this.r6(planTotal_ - cumulativeFact - factForDate)),
          assign_id:        work.assign_id   ?? null,
          contractor_name:  work.assign_name || '',
          contract_type:    '',
          work_guid:        row.usp_work_guid || '',
          breadcrumb:       '',
          is_complete:      this.isWorkComplete(row),
          materials: normMaterials.map((m) => {
            // Ищем сохранённое значение для этого материала
            const sv = savedMats.find((s) => s.material_id === m.material_id);
            return {
              material_id:        m.material_id,
              name:               sv ? (sv.material_name || m.material_name) : m.material_name,
              unit:               m.material_unit_name || '',
              norm:               sv?.consumption_rate ?? m.consumption_rate ?? 0,
              plan_amount:        parseFloat(m.material_amount) || 0,
              remainder:          Math.max(0, m.remainder != null ? this.r6(m.remainder) : this.r6((m.material_amount ?? 0) - (m.use_amount ?? 0))),
              use_amount:         m.use_amount ?? 0,
              value:              sv ? String(sv.material_value ?? 0) : '0',
              isManual:           sv?.is_manual ?? false,
              isReplaced:         !!(sv?.replaced_material_id),
              replacedMaterialId: sv?.replaced_material_id ?? null,
              purchaseTypeId:     m.purchase_type_id ?? null,
              isShow:             m.is_show !== false,
              isEdit:             !!m.is_edit,
            };
          }),
        } : null;

        const maxFactValue_ = this.factDialogMaxValue();
        const currentFact_  = this.parseFactNum(this.factDialog.factValue);
        if (currentFact_ > maxFactValue_) {
          this.factDialog.factValue    = String(maxFactValue_);
          this.factDialog.minFactValue = Math.min(this.factDialog.minFactValue || 0, maxFactValue_);
          if (this.factDialog.workData) {
            this.factDialog.workData.remainder = Math.max(0, this.r6(planTotal_ - cumulativeFact - maxFactValue_));
          }
          this.$q?.notify?.({ type: 'warning', message: 'Найден сохранённый факт выше плана. Значение ограничено остатком.' });
        }

        if (this.factDialog.workData?.materials?.length) {
          this.factDialog.materialsOpen = true;
        }

        if (isStorno) {
          this._applyStornoToFactDialog();
        }

        // Прогресс-бар = (выполнено ранее + факт за этот день) / план
        const plan = planTotal_;
        const val  = this.parseFactNum(this.factDialog.factValue);
        this.factDialog.factPct = plan > 0
          ? Math.min(100, Math.round(((cumulativeFact + val) / plan) * 100))
          : 0;
      } catch (e) {
        this.$q?.notify?.({ type: 'negative', message: 'Не удалось загрузить данные по работе' });
      } finally {
        if (loadId === this._ppmFactDlgLoadId) {
          this.factDialog.loading = false;
        }
      }
    },

    /** Инициализация сторно-данных диалога одной ячейки после загрузки работы */
    _applyStornoToFactDialog() {
      const mats = this.factDialog.workData?.materials;
      if (mats) {
        this.factDialog.originalMaterials = buildOriginalMaterialsFromWork(mats);
      }
      if (this.factDialog.uspFactId) {
        this.factDialog.correctionHistoryLoading = true;
        uspFactCorrectionRead({ usp_fact_id: this.factDialog.uspFactId })
          .then((rows) => { this.factDialog.correctionHistory = rows || []; })
          .catch(() => {})
          .finally(() => { this.factDialog.correctionHistoryLoading = false; });
      }
    },

    // ──────────────────────────────────────────────
    //  Групповой факт: конструктив → все дочерние работы
    // ──────────────────────────────────────────────

    /** Сумма фактов всех дочерних работ группы за указанную дату */
    getGroupCellSum(groupRow, dateKey) {
      if (!groupRow?.children?.length) return 0;
      return groupRow.children.reduce((sum, child) => {
        const val = this.getCellVal(child.id, dateKey);
        return sum + (parseFloat(val) || 0);
      }, 0);
    },

    /** Итоговая KZT-сумма дочерних работ за дату (работа + материалы) */
    getGroupCellKzt(groupRow, dateKey) {
      if (!groupRow?.children?.length) return null;
      let total = 0;
      let hasAny = false;
      for (const child of groupRow.children) {
        const meta = this.getCellMeta(child.id, dateKey);
        const price = meta?.totalPrice;
        if (price != null) {
          total += price;
          hasAny = true;
        }
      }
      return hasAny ? total : null;
    },

    collectWorkKeys(node, dateKey) {
      if (node.node_type === 'group') {
        return (node.children || [])
          .map((c) => c.id)
          .filter((id) => this.cellValues?.[id]?.[dateKey] != null || this.cellMeta?.[id]?.[dateKey] != null);
      }

      const floorIds = new Set();
      const walk = (n) => {
        if (n.node_type === 'floor') { floorIds.add(n.floor_map_id); return; }
        (n.children || []).forEach(walk);
      };
      if (node.node_type === 'floor') {
        floorIds.add(node.floor_map_id);
      } else {
        (node.children || []).forEach(walk);
      }

      const prefixes = [...floorIds].map((id) => `work_${id}_`);
      const seen = new Set();
      const keys = [];
      const scan = (store) => {
        for (const rowId of Object.keys(store)) {
          if (seen.has(rowId)) continue;
          for (const pfx of prefixes) {
            if (rowId.startsWith(pfx) && store[rowId]?.[dateKey] != null) {
              keys.push(rowId);
              seen.add(rowId);
              break;
            }
          }
        }
      };
      scan(this.cellValues);
      scan(this.cellMeta);
      return keys;
    },

    getParentCellSmr(row, dateKey) {
      const workKeys = this.collectWorkKeys(row, dateKey);
      let total = 0;
      for (const wid of workKeys) {
        const meta = this.cellMeta[wid]?.[dateKey];
        if (meta?.factPrice) total += meta.factPrice;
      }
      return total;
    },

    getParentCellTmc(row, dateKey) {
      const workKeys = this.collectWorkKeys(row, dateKey);
      let total = 0;
      for (const wid of workKeys) {
        const meta = this.cellMeta[wid]?.[dateKey];
        if (meta?.materialsPriceTotal) total += meta.materialsPriceTotal;
      }
      return total;
    },

    hasParentCellData(row, dateKey) {
      return this.collectWorkKeys(row, dateKey).length > 0;
    },

    /**
     * Возвращает статус завершённости дочерних работ конструктива:
     *   'all'     — все завершены
     *   'partial' — часть завершена
     *   'none'    — ни одна не завершена
     */
    getGroupCompletionStatus(groupRow) {
      const children = groupRow?.children || [];
      if (!children.length) return 'none';
      const done = children.filter((c) => this.isWorkComplete(c)).length;
      if (done === 0) return 'none';
      if (done === children.length) return 'all';
      return 'partial';
    },

    /** Открыть диалог группового факта */
    async openGroupFactDialog(groupRow, date) {
      const blockedWork = (groupRow.children || []).find((w) => {
        const k = `${w.floor_map_id}_${w.work_set_id}_${w.work_set_check_group_id || 0}`;
        return this.smetaChangeLog[k];
      });
      if (blockedWork) {
        const bk = `${blockedWork.floor_map_id}_${blockedWork.work_set_id}_${blockedWork.work_set_check_group_id || 0}`;
        this.smetaChangeDialog = {
          show: true,
          workName: groupRow.label || '',
          info: this.smetaChangeLog[bk],
        };
        return;
      }
      const loadId = ++this._ppmGfdLoadId;
      this.groupFactDialog = {
        show:      true,
        groupRow,
        date,
        loading:   true,
        saving:    false,
        workItems: [],
      };
      try {
        const children = groupRow.children || [];
        const items = await Promise.all(children.map(async (workRow) => {
          const saved = this.getCellVal(workRow.id, date.key);
          const meta  = this.getCellMeta(workRow.id, date.key);
          const item  = {
            row:          workRow,
            workData:     null,
            factValue:    saved !== null && saved !== '' ? String(saved) : '',
            factPct:      0,
            uspFactId:    null,
            isSent:       !!meta.isSent,
            minFactValue: 0,
            ...createStornoFields(saved),
          };
          try {
            const [workRes, factRes] = await Promise.all([
              getWorkDetail(workRow.floor_map_id, workRow.work_set_id, workRow.work_set_check_group_id),
              uspFactRead({
                project_guid:            this.projectGuid,
                floor_map_id:            workRow.floor_map_id,
                work_set_id:             workRow.work_set_id,
                work_set_check_group_id: workRow.work_set_check_group_id,
                value_date:              date.iso,
              }),
            ]);
            const work      = workRes?.work || null;
            const savedFact = this.pickUspFactForDate(this.uspFactRowsFromResponse(factRes), date.iso);

            if (savedFact) {
              item.factValue    = String(savedFact.fact_value ?? '');
              item.minFactValue = this.parseFactNum(savedFact.fact_value ?? 0);
              item.uspFactId    = savedFact.usp_fact_id ?? null;
              item.isSent       = !!savedFact.is_sent;
              this.setCellMeta(workRow.id, date.key, { isSent: !!savedFact.is_sent });

              if (this.stornoMode) {
                Object.assign(item, createStornoFields(savedFact.fact_value));
              }
            }

            if (work) {
              const planTotal      = work.work_set_amount ?? 0;
              const cumulativeFact = savedFact
                ? (savedFact.cumulative_fact ?? 0)
                : (work.use_amount ?? 0);
              const factForDate    = this.parseFactNum(item.factValue);

              const normMaterials = workRes?.materials || [];
              const savedMats     = savedFact?.materials || [];

              item.workData = {
                work_set_id:     work.work_set_id,
                unit:            work.work_set_unit_name || '',
                plan_total:      planTotal,
                cumulative_fact: cumulativeFact,
                remainder:       Math.max(0, this.r6(planTotal - cumulativeFact - factForDate)),
                assign_id:       work.assign_id ?? null,
                is_complete:     this.isWorkComplete(workRow),
                materials: normMaterials
                  .filter((m) => m.is_show !== false)
                  .map((m) => {
                    const sv      = savedMats.find((s) => s.material_id === m.material_id);
                    const autoVal = planTotal > 0
                      ? this.r6((parseFloat(m.material_amount) || 0) * (factForDate / planTotal))
                      : this.r6(factForDate * (m.consumption_rate ?? 0));
                    return {
                      material_id:        m.material_id,
                      name:               sv ? (sv.material_name || m.material_name) : m.material_name,
                      unit:               m.material_unit_name || '',
                      norm:               sv?.consumption_rate ?? m.consumption_rate ?? 0,
                      plan_amount:        parseFloat(m.material_amount) || 0,
                      remainder:          Math.max(0, m.remainder != null
                        ? this.r6(m.remainder)
                        : this.r6((m.material_amount ?? 0) - (m.use_amount ?? 0))),
                      use_amount:         m.use_amount ?? 0,
                      value:              sv ? String(sv.material_value ?? 0) : String(autoVal),
                      isManual:           sv?.is_manual ?? false,
                      isReplaced:         !!(sv?.replaced_material_id),
                      replacedMaterialId: sv?.replaced_material_id ?? null,
                      purchaseTypeId:     m.purchase_type_id ?? null,
                      isEdit:             !!m.is_edit,
                    };
                  }),
              };
              item.factPct = planTotal > 0
                ? Math.min(100, Math.round(((cumulativeFact + factForDate) / planTotal) * 100))
                : 0;
              if (work.work_set_amount != null) {
                workRow.work_set_amount = this.r6(work.work_set_amount);
              }

              if (this.stornoMode && item.workData?.materials) {
                item.originalMaterials = buildOriginalMaterialsFromWork(item.workData.materials);
              }
            }
          } catch { /* workData останется null — работа всё равно отобразится */ }
          return item;
        }));
        if (loadId !== this._ppmGfdLoadId) return;
        this.groupFactDialog.workItems = items;

      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Не удалось загрузить данные по конструктиву' });
      } finally {
        if (loadId === this._ppmGfdLoadId) {
          this.groupFactDialog.loading = false;
        }
      }
    },

    /** Вычисляет % из позиции курсора относительно ползунка */
    _groupBarPctFromEvent(e) {
      const bar = this.$refs.gfdBar;
      if (!bar) return null;
      const rect = bar.getBoundingClientRect();
      const x    = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
    },

    onGroupBarMouseDown(e) {
      const pct = this._groupBarPctFromEvent(e);
      if (pct !== null) this.onGroupPctChange(pct);
      const onMove = (ev) => {
        const p = this._groupBarPctFromEvent(ev);
        if (p !== null) this.onGroupPctChange(p);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup',   onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup',   onUp);
    },

    onGroupBarTouchStart(e) {
      const pct = this._groupBarPctFromEvent(e);
      if (pct !== null) this.onGroupPctChange(pct);
      const onMove = (ev) => {
        const p = this._groupBarPctFromEvent(ev);
        if (p !== null) this.onGroupPctChange(p);
      };
      const onEnd = () => {
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend',  onEnd);
      };
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend',  onEnd);
    },

    /** Единый % ползунок — пересчитывает все дочерние работы пропорционально их объёму */
    onGroupPctChange(pct) {
      pct = Math.max(this.groupCumulativePct, Math.min(100, pct));

      const items     = this.groupFactDialog.workItems;
      const totalPlan = items.reduce((s, i) => s + (i.workData?.plan_total || 0), 0);
      if (totalPlan <= 0) return;

      const targetTotal = this.r6((pct / 100) * totalPlan);

      const getMin = (i) => (i.workData?.cumulative_fact || 0) + (i.minFactValue || 0);

      let extra = Math.max(0, targetTotal - items.reduce((s, i) => s + getMin(i), 0));

      const editable  = items.filter((i) => !i.isSent && !i.workData?.is_complete && i.workData);
      const started   = editable.filter((i) => getMin(i) > 0);
      const unstarted = editable.filter((i) => getMin(i) === 0);

      editable.forEach((i) => { i._nv = getMin(i); });

      if (extra > 0 && started.length) {
        const startedCap = started.reduce((s, i) => s + Math.max(0, (i.workData.plan_total || 0) - getMin(i)), 0);
        if (startedCap > 0) {
          const fill = Math.min(extra, startedCap);
          started.forEach((i) => {
            const cap = Math.max(0, (i.workData.plan_total || 0) - getMin(i));
            i._nv = this.r6(getMin(i) + fill * (cap / startedCap));
          });
          extra = Math.max(0, extra - fill);
        }
      }

      if (extra > 0 && unstarted.length) {
        const unstartedCap = unstarted.reduce((s, i) => s + (i.workData.plan_total || 0), 0);
        if (unstartedCap > 0) {
          unstarted.forEach((i) => {
            const cap = i.workData.plan_total || 0;
            i._nv = this.r6(extra * (cap / unstartedCap));
          });
        }
      }

      editable.forEach((item) => {
        const plan         = item.workData.plan_total      || 0;
        const cumulative   = item.workData.cumulative_fact || 0;
        const minF         = item.minFactValue || 0;
        const totalDone    = Math.max(getMin(item), Math.min(plan, item._nv ?? getMin(item)));
        delete item._nv;
        const maxFactToday = Math.max(0, this.r6(plan - cumulative));
        let factToday      = Math.max(minF, this.r6(totalDone - cumulative));
        if (factToday > maxFactToday) factToday = maxFactToday;
        item.factValue          = String(this.r6(factToday));
        item.factPct            = plan > 0
          ? Math.min(100, Math.round(((cumulative + factToday) / plan) * 100))
          : 0;
        item.workData.remainder = Math.max(0, this.r6(plan - cumulative - factToday));
        this._recalcGroupItemMaterials(item, factToday);
      });
    },

    /** Пересчёт материалов конкретного workItem пропорционально factNum */
    _recalcGroupItemMaterials(item, factNum) {
      const workPlan = item.workData?.plan_total || 0;
      (item.workData?.materials || []).forEach((m) => {
        if (!m.isManual) {
          const raw = workPlan > 0
            ? this.r6(m.plan_amount * (factNum / workPlan))
            : this.r6(factNum * m.norm);
          m.value = this.clampMaterialToZhcpRemainder(m, raw, false);
        }
      });
    },

    /** Изменение факта для отдельной работы внутри группового диалога */
    onGroupItemValueInput(item, val) {
      if (item.isSent || item.workData?.is_complete) return;
      if (item.workData) {
        const plan         = item.workData.plan_total      || 0;
        const cumulative   = item.workData.cumulative_fact || 0;
        const minF         = item.minFactValue || 0;
        const maxFactToday = Math.max(0, this.r6(plan - cumulative));
        let num            = this.parseFactNum(val);
        if (Number.isNaN(num) || num < 0) num = 0;
        if (num < minF) num = minF;
        if (num > maxFactToday) {
          num = maxFactToday;
          this.$q?.notify?.({
            type:    'warning',
            message: 'Не больше остатка по работе (план минус выполнено ранее)',
          });
        }
        item.factValue = String(this.r6(num));
        item.factPct   = plan > 0 ? Math.min(100, Math.round(((cumulative + num) / plan) * 100)) : 0;
        item.workData.remainder = Math.max(0, this.r6(plan - cumulative - num));
        this._recalcGroupItemMaterials(item, num);
      } else {
        item.factValue = String(this.parseFactNum(val));
      }
    },

    /** Ручное изменение материала в групповом диалоге */
    onGroupMatInput(item, mat, val) {
      if (item.isSent || item.workData?.is_complete) return;
      mat.value    = this.clampMaterialToZhcpRemainder(mat, val, true);
      mat.isManual = true;
    },

    /** Сброс ручного значения материала к автоматическому расчёту */
    resetGroupMaterial(item, mat) {
      const num      = this.parseFactNum(item.factValue);
      const workPlan = item.workData?.plan_total || 0;
      const raw      = workPlan > 0
        ? this.r6(mat.plan_amount * (num / workPlan))
        : this.r6(num * mat.norm);
      mat.value    = this.clampMaterialToZhcpRemainder(mat, raw, false);
      mat.isManual = false;
    },

    async submitGroupStornoDialog() {
      const { groupRow, date, workItems } = this.groupFactDialog;
      if (!groupRow || !date) return;

      const itemsToSave = workItems.filter((item) => this._hasStornoChanges(item));

      if (!itemsToSave.length) {
        this.$q?.notify?.({ type: 'warning', message: STORNO_MESSAGES.NO_CHANGES });
        return;
      }

      this.groupFactDialog.saving = true;
      try {
        let savedCorrections = 0;
        let appliedDirect    = 0;
        await Promise.all(itemsToSave.map(async (item) => {
          const row     = item.row;
          const origNum = this.parseFactNum(item.originalFactValue);
          let newNum    = this._clampNewFactByPlan(
            item.newFactValue,
            item.workData?.plan_total,
            item.workData?.cumulative_fact,
          );
          if (newNum < 0) newNum = 0;
          const diff = this.r6(newNum - origNum);

          const triplet = buildMaterialsJsonTriplet({
            originalMaterials: item.originalMaterials,
            workMaterials:     item.workData?.materials,
            parseFactNum:      this.parseFactNum,
            r6:                this.r6,
          });

          if (!item.isSent) {
            await this._applyStornoUnsent({
              row,
              date,
              uspFactId:         item.uspFactId,
              newNum,
              workData:          item.workData,
              originalMaterials: item.originalMaterials,
            });
            this.setCellMeta(row.id, date.key, { hasPendingCorrection: false });
            appliedDirect++;
            return;
          }

          await uspFactCorrectionSave({
            usp_fact_id:             item.uspFactId,
            project_guid:            this.projectGuid,
            floor_map_id:            row.floor_map_id,
            work_set_id:             row.work_set_id,
            work_set_check_group_id: row.work_set_check_group_id,
            value_date:              date.iso,
            usp_work_guid:           row.usp_work_guid ?? null,
            original_fact_value:     origNum,
            new_fact_value:          newNum,
            diff_fact_value:         diff,
            original_materials_json: triplet.originalMaterialsJson,
            new_materials_json:      triplet.newMaterialsJson,
            diff_materials_json:     triplet.diffMaterialsJson,
          });

          this.setCellMeta(row.id, date.key, { hasPendingCorrection: true });
          savedCorrections++;
        }));

        const parts = [];
        if (savedCorrections) parts.push(`корректировок: ${savedCorrections} (${STORNO_MESSAGES.WILL_APPLY_ON_SEND})`);
        if (appliedDirect)    parts.push(`применено сразу: ${appliedDirect}`);
        this.$q?.notify?.({
          type:    'positive',
          message: `Групповая корректировка: ${parts.join('; ')}`,
          timeout: 5000,
        });
        this.groupFactDialog.show = false;
      } catch (e) {
        const firstItem = itemsToSave[0];
        if (firstItem && this.handleSmetaBlockedError(e, firstItem.row)) return;
        this.$q?.notify?.({ type: 'negative', message: STORNO_MESSAGES.GROUP_SAVE_ERROR });
      } finally {
        this.groupFactDialog.saving = false;
      }
    },

    /** Есть ли у item реальные изменения (факт или материалы) */
    _hasStornoChanges(item) {
      if (!item.workData || !item.uspFactId) return false;
      const origFact = this.parseFactNum(item.originalFactValue);
      const newFact  = this.parseFactNum(item.newFactValue);
      if (this.r6(newFact - origFact) !== 0) return true;
      return (item.originalMaterials || []).some((om) => {
        return this.r6(this.parseFactNum(om.newValue) - this.parseFactNum(om.originalValue)) !== 0;
      });
    },

    /** Сохранить факт по всем работам группы */
    async submitGroupFactDialog() {
      const { groupRow, date, workItems } = this.groupFactDialog;
      if (!groupRow || !date) return;

      const itemsToSave = workItems.filter(
        (item) => item.workData && !item.isSent && !item.workData.is_complete && item.factValue !== '',
      );
      if (!itemsToSave.length) {
        this.$q?.notify?.({ type: 'warning', message: 'Нет данных для сохранения' });
        return;
      }

      for (const item of itemsToSave) {
        const bad = this.materialsExceedingZhcpRemainder(item.workData?.materials);
        if (bad.length) {
          const msg = bad.map((b) => `${b.name}: макс. ${this.round6(b.cap)} ${b.unit}`).join('; ');
          this.$q?.notify?.({
            type:    'negative',
            message: `Превышен остаток по ЖЦП (${item.row?.label || 'работа'}): ${msg}`,
            timeout: 6000,
          });
          return;
        }
      }

      this.groupFactDialog.saving = true;
      try {
        await Promise.all(itemsToSave.map(async (item) => {
          const row        = item.row;
          const planNum    = this.parseFactNum(item.workData?.plan_total);
          const cumulative = this.parseFactNum(item.workData?.cumulative_fact);
          const maxFact    = Math.max(0, this.r6(planNum - cumulative));
          const factNum    = Math.min(maxFact, Math.max(item.minFactValue, this.parseFactNum(item.factValue)));
          const factPct    = planNum > 0
            ? Math.min(100, Math.round(((cumulative + factNum) / planNum) * 100))
            : 0;

          await uspFactSave({
            project_guid:            this.projectGuid,
            floor_map_id:            row.floor_map_id,
            work_set_id:             row.work_set_id,
            work_set_check_group_id: row.work_set_check_group_id,
            value_date:              date.iso,
            fact_value:              factNum,
            fact_pct:                factPct,
            usp_work_guid:           row.usp_work_guid ?? null,
            assign_id:               item.workData?.assign_id ?? null,
            materials: (item.workData?.materials || []).map((m) => ({
              material_id:          m.material_id,
              replaced_material_id: m.replacedMaterialId ?? null,
              material_name:        m.name,
              material_value:       this.parseFactNum(m.value),
              is_manual:            !!m.isManual,
              consumption_rate:     m.norm ?? 0,
              purchase_type_id:     m.purchaseTypeId ?? null,
            })),
          });

          this.setCellVal(row.id, date.key, String(factNum));
          this.setCellMeta(row.id, date.key, { isSent: false });

          // Авто-завершение при 100%
          if (factPct >= 100 && !this.isWorkComplete(row)) {
            try {
              await uspWorkComplete({
                project_guid:            this.projectGuid,
                floor_map_id:            row.floor_map_id,
                work_set_id:             row.work_set_id,
                work_set_check_group_id: row.work_set_check_group_id,
                is_ready:                true,
              });
              const newCompleted = new Set(this.completedWorks);
              newCompleted.add(`${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id}`);
              this.completedWorks = newCompleted;
            } catch { /* тихо */ }
          }
        }));

        // Перечитываем цены из БД (factPrice, totalPrice и др. для KZT-режима)
        await Promise.all([this.loadSavedValues(), this.loadCumulativeFacts()]);
        this.$q?.notify?.({ type: 'positive', message: `Факт сохранён по ${itemsToSave.length} работам` });
        this.groupFactDialog.show = false;
      } catch (e) {
        if (this.handleSmetaBlockedError(e, groupRow)) return;
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка сохранения' });
      } finally {
        this.groupFactDialog.saving = false;
      }
    },

    _recalcMaterials(factNum) {
      const mats     = this.factDialog.workData?.materials;
      if (!mats) return;
      const workPlan = this.parseFactNum(this.factDialog.workData?.plan_total);
      mats.forEach((m) => {
        if (!m.isManual) {
          const auto = workPlan > 0
            ? this.r6(m.plan_amount * (factNum / workPlan))
            : this.r6(factNum * m.norm);
          m.value = this.clampMaterialToZhcpRemainder(m, auto, false);
        }
      });
    },
    onFactPctChange(pct) {
      if (this.factDialog.isSent) return;
      const plan       = this.factDialog.workData?.plan_total || 0;
      const cumulative = this.factDialog.workData?.cumulative_fact || 0;
      const minVal     = this.factDialog.minFactValue || 0;
      const maxVal     = this.factDialogMaxValue();
      if (plan > 0) {
        // Вычисляем факт за этот день, зажатый снизу минимумом
        const totalForPct = this.r6((pct / 100) * plan);
        const newVal      = Math.min(maxVal, Math.max(minVal, this.r6(totalForPct - cumulative)));
        this.factDialog.factValue = String(newVal);
        // Пересчитываем factPct из реального newVal, чтобы ползунок не уходил ниже замка
        this.factDialog.factPct   = Math.min(100, Math.round(((cumulative + newVal) / plan) * 100));
        if (this.factDialog.workData) {
          this.factDialog.workData.remainder = Math.max(0, this.r6(plan - cumulative - newVal));
        }
        this._recalcMaterials(newVal);
      }
    },
    _barPctFromEvent(e) {
      const bar = this.$refs.fworkBar;
      if (!bar) return null;
      const rect = bar.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
    },
    onBarMouseDown(e) {
      if (this.factDialog.isSent) return;
      const pct = this._barPctFromEvent(e);
      if (pct !== null) { this.onFactPctChange(pct); }
      const onMove = (ev) => {
        const p = this._barPctFromEvent(ev);
        if (p !== null) { this.onFactPctChange(p); }
      };
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    onBarTouchStart(e) {
      if (this.factDialog.isSent) return;
      const pct = this._barPctFromEvent(e);
      if (pct !== null) { this.onFactPctChange(pct); }
      const onMove = (ev) => {
        const p = this._barPctFromEvent(ev);
        if (p !== null) { this.onFactPctChange(p); }
      };
      const onEnd = () => { document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onEnd); };
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend', onEnd);
    },
    onFactValueInput(val) {
      if (this.factDialog.isSent) return;
      const minVal = this.factDialog.minFactValue || 0;
      const maxVal = this.factDialogMaxValue();
      let num      = this.parseFactNum(val);
      if (num < minVal) num = minVal;
      if (num > maxVal) num = maxVal;
      num = this.r6(num);
      this.factDialog.factValue = String(num);
      const plan       = this.factDialog.workData?.plan_total || 0;
      const cumulative = this.factDialog.workData?.cumulative_fact || 0;
      this.factDialog.factPct = plan > 0
        ? Math.min(100, Math.round(((cumulative + num) / plan) * 100))
        : 0;
      if (this.factDialog.workData) {
        this.factDialog.workData.remainder = Math.max(0, this.r6(plan - cumulative - num));
      }
      this._recalcMaterials(num);
    },
    /** Верхняя граница для type=number (остаток ЖЦП), если известна */
    materialZhcpMax(mat) {
      if (mat?.remainder == null || mat.remainder === '') return undefined;
      return Math.max(0, this.parseFactNum(mat.remainder));
    },

    /** Ограничение количества материала остатком по ЖЦП (mat.remainder) */
    clampMaterialToZhcpRemainder(mat, rawVal, notifyIfClamped) {
      let num = this.parseFactNum(rawVal);
      if (num < 0) num = 0;
      if (mat?.remainder == null || mat.remainder === '') return String(this.r6(num));
      const rem = this.parseFactNum(mat.remainder);
      const cap = this.r6(Math.max(0, rem));
      if (num > cap + 1e-6) {
        if (notifyIfClamped) {
          this.$q?.notify?.({
            type:    'warning',
            message: `Не больше остатка по ЖЦП: ${this.round6(cap)} ${mat.unit || ''}`.trim(),
          });
        }
        return String(cap);
      }
      return String(this.r6(num));
    },
    /** Материалы, у которых value превышает remainder (если remainder задан) */
    materialsExceedingZhcpRemainder(materials) {
      const out = [];
      for (const m of materials || []) {
        const v   = this.parseFactNum(m.value);
        if (m?.remainder == null || m.remainder === '') continue;
        const rem = this.parseFactNum(m.remainder);
        const cap = Math.max(0, rem);
        if (v > cap + 1e-6) {
          out.push({ name: m.name, cap, unit: m.unit || '', value: v });
        }
      }
      return out;
    },

    onMaterialInput(mat, val) {
      if (this.factDialog.isSent) return;
      mat.value    = this.clampMaterialToZhcpRemainder(mat, val, true);
      mat.isManual = true;

      if (!this.factDialog.matSyncFact) return;

      const num  = this.parseFactNum(mat.value);
      const norm = mat.norm || 0;
      if (norm > 0) {
        const newFact = this.r6(num / norm);
        this.factDialog.factValue = String(newFact);

        const plan       = this.factDialog.workData?.plan_total || 0;
        const cumulative = this.factDialog.workData?.cumulative_fact || 0;
        this.factDialog.factPct = plan > 0
          ? Math.min(100, Math.round(((cumulative + newFact) / plan) * 100))
          : 0;
        if (this.factDialog.workData) {
          this.factDialog.workData.remainder = Math.max(0, this.r6(plan - cumulative - newFact));
        }

        const mats     = this.factDialog.workData?.materials || [];
        const workPlan = this.parseFactNum(this.factDialog.workData?.plan_total);
        mats.forEach((m) => {
          if (m.isManual) return;
          const raw = workPlan > 0 ? this.r6(m.plan_amount * (newFact / workPlan)) : this.r6(newFact * m.norm);
          m.value = this.clampMaterialToZhcpRemainder(m, raw, false);
        });
      }
    },
    requestCompleteWork(rows) {
      if (!rows?.length) return;
      // Вычисляем суммарный % для каждой работы из видимых ячеек
      const hasUnder100 = rows.some((r) => {
        if (!r.work_set_amount || r.work_set_amount <= 0) return false;
        const pct = Math.round((this.getFactSum(r.id) / r.work_set_amount) * 100);
        return pct < 100;
      });
      this.completeDialog.rows = rows;
      if (hasUnder100) {
        this.completeDialog.show = true;
      } else {
        this.doCompleteWork();
      }
    },

    async doCompleteWork() {
      const rows = this.completeDialog.rows;
      if (!rows?.length) return;
      this.completeDialog.loading = true;
      try {
        await Promise.all(rows.map((r) =>
          uspWorkComplete({
            project_guid:            this.projectGuid,
            floor_map_id:            r.floor_map_id,
            work_set_check_group_id: r.work_set_check_group_id,
            work_set_id:             r.work_set_id,
            is_ready:                true,
          })
        ));
        this.$q?.notify?.({ type: 'positive', message: `Завершено работ: ${rows.length}` });
        // Помечаем локально как завершённые
        const newCompleted = new Set(this.completedWorks);
        rows.forEach((r) => newCompleted.add(`${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id}`));
        this.completedWorks = newCompleted;
        // Снимаем выбор завершённых
        const doneIds = rows.map((r) => r.id);
        this.selectedWorkIds = this.selectedWorkIds.filter((id) => !doneIds.includes(id));
        this.completeDialog.show = false;
        // Закрываем модалку факта, если завершение из неё
        if (this.factDialog.show && doneIds.includes(this.factDialog.row?.id)) {
          this.factDialog.show = false;
        }
      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка при завершении работ' });
      } finally {
        this.completeDialog.loading = false;
      }
    },

    async fillWorkGuids() {
      this.fillGuidLoading = true;
      try {
        await uspFactFillWorkGuid(this.projectGuid);
      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка при заполнении work_guid' });
        this.fillGuidLoading = false;
        return;
      }
      this.$q?.notify?.({ type: 'positive', message: 'work_guid успешно заполнен' });
      try {
        await this.loadData();
      } catch {
        // игнорируем ошибку перезагрузки
      } finally {
        this.fillGuidLoading = false;
      }
    },

    requestUncompleteWork(row) {
      this.$q?.dialog?.({
        title:   'Снять завершение',
        message: 'Снять отметку о завершении работы?',
        cancel:  { label: 'Отмена', flat: true },
        ok:      { label: 'Снять', color: 'orange-8', flat: true },
      }).onOk(() => this.doUncompleteWork(row));
    },

    async doUncompleteWork(row) {
      try {
        await uspWorkComplete({
          project_guid:            this.projectGuid,
          floor_map_id:            row.floor_map_id,
          work_set_check_group_id: row.work_set_check_group_id,
          work_set_id:             row.work_set_id,
          is_ready:                false,
        });
        const key = `${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id}`;
        const newCompleted = new Set(this.completedWorks);
        newCompleted.delete(key);
        this.completedWorks = newCompleted;
        this.$q?.notify?.({ type: 'warning', message: 'Завершение снято' });
      } catch {
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка при снятии завершения' });
      }
    },

    resetMaterial(mat) {
      const num      = this.parseFactNum(this.factDialog.factValue);
      const workPlan = this.parseFactNum(this.factDialog.workData?.plan_total);
      const raw      = workPlan > 0 ? this.r6(mat.plan_amount * (num / workPlan)) : this.r6(num * mat.norm);
      mat.value              = this.clampMaterialToZhcpRemainder(mat, raw, false);
      mat.isManual           = false;
      mat.isReplaced         = false;
      mat.replacedMaterialId = null;
    },
    resetAllMaterials() {
      const num      = this.parseFactNum(this.factDialog.factValue);
      const workPlan = this.parseFactNum(this.factDialog.workData?.plan_total);
      (this.factDialog.workData?.materials || []).forEach((m) => {
        const raw = workPlan > 0 ? this.r6(m.plan_amount * (num / workPlan)) : this.r6(num * m.norm);
        m.value              = this.clampMaterialToZhcpRemainder(m, raw, false);
        m.isManual           = false;
        m.isReplaced         = false;
        m.replacedMaterialId = null;
      });
    },
    async deleteFactRow() {
      const { row, date, uspFactId } = this.factDialog;
      if (!uspFactId) return;

      this.factDialog.deleting = true;
      try {
        await uspFactDelete({ usp_fact_id: uspFactId });

        // Сбрасываем ячейку в матрице
        this.setCellVal(row.id, date.key, null);
        this.setCellMeta(row.id, date.key, { isSent: false });

        // Сбрасываем диалог в режим ввода (не закрываем)
        this.factDialog.uspFactId    = null;
        this.factDialog.isSent       = false;
        this.factDialog.factValue    = '';
        this.factDialog.factPct      = 0;
        this.factDialog.minFactValue = 0;
        if (this.factDialog.workData) {
          const plan = this.factDialog.workData.plan_total || 0;
          const cum  = this.factDialog.workData.cumulative_fact || 0;
          this.factDialog.workData.remainder = Math.max(0, this.r6(plan - cum));
          this._recalcMaterials(0);
        }

        this.$q?.notify?.({ type: 'positive', message: 'Запись удалена. Можете ввести новые данные.', timeout: 3000 });

        await Promise.all([this.loadSavedValues(), this.loadCumulativeFacts()]);
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || 'Ошибка удаления';
        this.$q?.notify?.({ type: 'negative', message: msg, timeout: 5000 });
      } finally {
        this.factDialog.deleting = false;
      }
    },

    async deleteFactByCell(row, date) {
      const meta = this.getCellMeta(row.id, date.key);
      const saved = this.getCellVal(row.id, date.key);
      if (!saved && saved !== 0) return;

      this.$q?.dialog?.({
        title:   'Удалить факт',
        message: `Удалить запись факта за ${date.label || date.iso}?`,
        cancel:  { label: 'Отмена', flat: true },
        ok:      { label: 'Удалить', color: 'negative', flat: true },
      }).onOk(async () => {
        try {
          const factRes = await uspFactRead({
            project_guid:             this.projectGuid,
            floor_map_id:             row.floor_map_id,
            work_set_id:              row.work_set_id,
            work_set_check_group_id:  row.work_set_check_group_id,
            value_date:               date.iso,
          });
          const rows_ = this.uspFactRowsFromResponse(factRes);
          const savedFact = this.pickUspFactForDate(rows_, date.iso);
          if (!savedFact?.usp_fact_id) {
            this.$q?.notify?.({ type: 'warning', message: 'Запись не найдена в БД' });
            return;
          }
          await uspFactDelete({ usp_fact_id: savedFact.usp_fact_id });
          this.setCellVal(row.id, date.key, null);
          this.setCellMeta(row.id, date.key, { isSent: false });
          this.$q?.notify?.({ type: 'positive', message: 'Запись удалена' });
          await Promise.all([this.loadSavedValues(), this.loadCumulativeFacts()]);
        } catch (e) {
          const msg = e?.response?.data?.error || e?.message || 'Ошибка удаления';
          this.$q?.notify?.({ type: 'negative', message: msg, timeout: 5000 });
        }
      });
    },

    async submitStornoDialog() {
      const { row, date, newFactValue, originalFactValue, workData, originalMaterials, uspFactId, isSent } = this.factDialog;
      if (!row || !date) return;

      const origNum = this.parseFactNum(originalFactValue);
      let newNum    = this._clampNewFactByPlan(
        newFactValue,
        workData?.plan_total,
        workData?.cumulative_fact,
      );
      if (newNum < 0) newNum = 0;
      const diff = this.r6(newNum - origNum);

      const triplet = buildMaterialsJsonTriplet({
        originalMaterials,
        workMaterials: workData?.materials,
        parseFactNum:  this.parseFactNum,
        r6:            this.r6,
      });
      const matsChanged = hasMaterialsDiff(triplet.diffMaterialsJson);

      if (diff === 0 && !matsChanged) {
        this.$q?.notify?.({ type: 'warning', message: STORNO_MESSAGES.NO_CHANGES });
        return;
      }

      this.factDialog.saving = true;
      try {
        if (!isSent) {
          await this._applyStornoUnsent({
            row,
            date,
            uspFactId,
            newNum,
            workData,
            originalMaterials,
          });
          this.setCellMeta(row.id, date.key, { hasPendingCorrection: false });
          this.$q?.notify?.({
            type:    'positive',
            message: newNum === 0 ? 'Факт удалён (не был отправлен в УСП)' : 'Факт обновлён',
          });
        } else {
          await uspFactCorrectionSave({
            usp_fact_id:             uspFactId,
            project_guid:            this.projectGuid,
            floor_map_id:            row.floor_map_id,
            work_set_id:             row.work_set_id,
            work_set_check_group_id: row.work_set_check_group_id,
            value_date:              date.iso,
            usp_work_guid:           row.usp_work_guid ?? null,
            original_fact_value:     origNum,
            new_fact_value:          newNum,
            diff_fact_value:         diff,
            original_materials_json: triplet.originalMaterialsJson,
            new_materials_json:      triplet.newMaterialsJson,
            diff_materials_json:     triplet.diffMaterialsJson,
          });
          this.setCellMeta(row.id, date.key, { hasPendingCorrection: true });
          this._notifyCorrectionSaved(diff, newNum);
        }
        this.factDialog.show = false;
      } catch (e) {
        if (this.handleSmetaBlockedError(e, row)) return;
        this.$q?.notify?.({ type: 'negative', message: STORNO_MESSAGES.SAVE_ERROR });
      } finally {
        this.factDialog.saving = false;
      }
    },

    /**
     * Применить сторно напрямую к usp_fact_tab (для неотправленных фактов):
     *   newNum === 0 → uspFactDelete (полное удаление)
     *   newNum !== 0 → uspFactSave с обновлёнными материалами
     */
    async _applyStornoUnsent({ row, date, uspFactId, newNum, workData, originalMaterials }) {
      if (newNum === 0) {
        await uspFactDelete({ usp_fact_id: uspFactId });
        this.setCellVal(row.id, date.key, '');
        this.setCellMeta(row.id, date.key, { isSent: false, factPrice: null, materialsPriceTotal: 0, totalPrice: null });
        await Promise.all([this.loadSavedValues(), this.loadCumulativeFacts()]);
        return;
      }

      const planNum    = this.parseFactNum(workData?.plan_total);
      const cumulative = this.parseFactNum(workData?.cumulative_fact);
      const factPct    = planNum > 0
        ? Math.min(100, Math.round(((cumulative + newNum) / planNum) * 100))
        : 0;

      const materials = (workData?.materials || []).map((m) => {
        const om = (originalMaterials || []).find((o) => o.material_id === m.material_id);
        const newVal = om ? this.parseFactNum(om.newValue) : this.parseFactNum(m.value);
        return {
          material_id:          m.material_id,
          replaced_material_id: m.replacedMaterialId ?? null,
          material_name:        m.name,
          material_value:       newVal,
          is_manual:            !!m.isManual,
          consumption_rate:     m.norm ?? 0,
          purchase_type_id:     m.purchaseTypeId ?? null,
        };
      });

      await uspFactSave({
        project_guid:            this.projectGuid,
        floor_map_id:            row.floor_map_id,
        work_set_id:             row.work_set_id,
        work_set_check_group_id: row.work_set_check_group_id,
        value_date:              date.iso,
        fact_value:              newNum,
        fact_pct:                factPct,
        usp_work_guid:           row.usp_work_guid ?? null,
        assign_id:               workData?.assign_id ?? null,
        materials,
      });

      this.setCellVal(row.id, date.key, String(newNum));
      this.setCellMeta(row.id, date.key, { isSent: false });
      await Promise.all([this.loadSavedValues(), this.loadCumulativeFacts()]);
    },

    async submitFactDialog() {
      const { row, date, factValue, workData } = this.factDialog;
      if (!row || !date) return;

      const badMats = this.materialsExceedingZhcpRemainder(workData?.materials);
      if (badMats.length) {
        const msg = badMats.map((b) => `${b.name}: макс. ${this.round6(b.cap)} ${b.unit}`).join('; ');
        this.$q?.notify?.({
          type:    'negative',
          message: `Превышен остаток по ЖЦП: ${msg}`,
          timeout: 6000,
        });
        return;
      }

      this.factDialog.saving = true;
      try {
        let factNum      = this.parseFactNum(factValue);
        const planNum    = this.parseFactNum(workData?.plan_total);
        const cumulative = this.parseFactNum(workData?.cumulative_fact);
        const maxFactNum = this.factDialogMaxValue();
        if (factNum > maxFactNum) {
          factNum = maxFactNum;
          this.factDialog.factValue = String(maxFactNum);
          if (this.factDialog.workData) {
            this.factDialog.workData.remainder = Math.max(0, this.r6(planNum - cumulative - maxFactNum));
          }
          this.$q?.notify?.({ type: 'warning', message: 'Факт был ограничен остатком по работе' });
        }
        // factPct в БД хранит суммарный процент (накоплено + сегодня)
        const factPct    = planNum > 0
          ? Math.min(100, Math.round(((cumulative + factNum) / planNum) * 100))
          : 0;
        const materials = (workData?.materials || []).map((m) => ({
          material_id:          m.material_id,
          replaced_material_id: m.replacedMaterialId ?? null,
          material_name:        m.name,
          material_value:       this.parseFactNum(m.value),
          is_manual:            !!m.isManual,
          consumption_rate:     m.norm ?? 0,
          purchase_type_id:     m.purchaseTypeId ?? null,
        }));

        await uspFactSave({
          project_guid:             this.projectGuid,
          floor_map_id:             row.floor_map_id,
          work_set_id:              row.work_set_id,
          work_set_check_group_id:  row.work_set_check_group_id,
          value_date:               date.iso,
          fact_value:               factNum,
          fact_pct:                 factPct,
          usp_work_guid:            row.usp_work_guid ?? null,
          assign_id:                workData?.assign_id ?? null,
          materials,
        });

        row.work_set_amount = this.r6(planNum);

        // Авто-завершение при достижении 100%
        if (factPct >= 100 && !this.isWorkComplete(row)) {
          try {
            await uspWorkComplete({
              project_guid:            this.projectGuid,
              floor_map_id:            row.floor_map_id,
              work_set_id:             row.work_set_id,
              work_set_check_group_id: row.work_set_check_group_id,
              is_ready:                true,
            });
            const newCompleted = new Set(this.completedWorks);
            newCompleted.add(`${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id}`);
            this.completedWorks = newCompleted;
          } catch { /* тихо — основной факт уже сохранён */ }
        }

        // Обновляем ячейку в матрице
        this.setCellVal(row.id, date.key, String(factNum));
        // После записи нового факта — не отправлено (сбрасываем is_sent)
        this.setCellMeta(row.id, date.key, { isSent: false });
        // Перечитываем цены из БД (factPrice, totalPrice и др. для KZT-режима)
        await Promise.all([this.loadSavedValues(), this.loadCumulativeFacts()]);
        this.$q?.notify?.({ type: 'positive', message: 'Факт сохранён' });
        this.factDialog.show = false;
      } catch (e) {
        if (this.handleSmetaBlockedError(e, row)) return;
        this.$q?.notify?.({ type: 'negative', message: 'Ошибка сохранения факта' });
      } finally {
        this.factDialog.saving = false;
      }
    },
    handleSmetaBlockedError(e, row) {
      const resp = e?.response;
      if (resp?.status === 409 && resp?.data?.smeta_blocked) {
        const info = resp.data.change_info || {};
        this.smetaChangeDialog = {
          show: true,
          workName: row?.label || row?.fullLabel || '',
          info,
        };
        const key = `${row.floor_map_id}_${row.work_set_id}_${row.work_set_check_group_id || 0}`;
        if (key && row.floor_map_id != null) {
          this.smetaChangeLog[key] = info;
        }
        this.loadSmetaChangeLog();
        this.factDialog.show = false;
        this.factDialog.saving = false;
        this.groupFactDialog.show = false;
        this.groupFactDialog.saving = false;
        return true;
      }
      return false;
    },
    // ──────────────────────────────────────────────
    //  Утилиты
    // ──────────────────────────────────────────────
    // parseIso, isoDate — provided by useFormatters via setup()
  },
};
</script>

<style>
/* ============================================================
   Layout
   ============================================================ */
.ppm-page {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 116px);
  overflow: hidden;
  box-sizing: border-box;
}
.ppm-page--fullscreen {
  position: fixed;
  inset: 0;
  z-index: 5000;
  height: 100vh;
  padding: 8px;
  background: #fff;
}

/* ============================================================
   Header
   ============================================================ */
.ppm-header {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
  flex-shrink: 0;
}
.ppm-header__row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.ppm-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 3px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.ppm-header__btn { border-radius: 6px; }
.ppm-header__btn--primary { box-shadow: none; }
.ppm-period-wrap {
  cursor: pointer;
  position: relative;
}
.ppm-header :deep(.q-field) {
  font-size: 12px;
}
.ppm-header :deep(.q-field__control) {
  border-radius: 4px;
}
.ppm-header :deep(.q-field--outlined .q-field__control:before) {
  border: 1px solid #e5e7eb !important;
}
.ppm-header :deep(.q-field--outlined .q-field__control:hover:before) {
  border-color: #d1d5db !important;
}
.ppm-header :deep(.q-field--outlined.q-field--focused .q-field__control:after) {
  border-color: #94a3b8 !important;
  border-width: 1px !important;
}
.ppm-header :deep(.q-field__label) {
  font-size: 12px;
  top: 12px;
}
.ppm-header :deep(.q-field__native),
.ppm-header :deep(.q-field__input) {
  font-size: 12px;
}
.ppm-header :deep(.q-select__dropdown-icon) {
  font-size: 16px;
  color: #9ca3af;
}
.ppm-header :deep(.q-field__append) {
  padding-left: 2px;
}
.ppm-header :deep(.q-field__prepend .q-btn) {
  font-size: 16px;
}
.ppm-clear-icon {
  color: #9ca3af;
  border-radius: 50%;
  padding: 2px;
  transition: color .15s, background .15s;
}
.ppm-clear-icon:hover {
  color: #374151;
  background: rgba(0,0,0,.06);
}
.expand-all-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0 2px;
  border-top: 1px solid #eee;
}
.expand-all-progress__icon {
  animation: spin 1s linear infinite;
}
.expand-all-progress__text {
  font-size: 12px;
  color: #555;
  white-space: nowrap;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}


/* ============================================================
   Background warning check indicator
   ============================================================ */
.ppm-warn-check {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 12px;
  font-size: 12px;
  color: #e6a117;
  background: #fff8e1;
  border-bottom: 1px solid #ffe082;
}
.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ============================================================
   Empty state
   ============================================================ */
.ppm-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #999;
  font-size: 14px;
}

/* ============================================================
   Table wrapper
   ============================================================ */
/* ============================================================
   Wrap
   ============================================================ */
.ppm-table-wrap {
  flex: 1;
  overflow: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
  min-height: 0;
}

/* ============================================================
   Table base
   ============================================================ */
.ppm-table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 100%;
  table-layout: auto;
  font-size: 13px;
  white-space: nowrap;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}
.ppm-table th,
.ppm-table td {
  border-right: 1px solid #f1f3f5;
  border-bottom: 1px solid #f1f3f5;
  padding: 0;
}
.ppm-table th:first-child,
.ppm-table td:first-child { border-left: none; }
.ppm-table thead tr:first-child th { border-top: none; }

/* --- Sticky header --- */
.ppm-table thead th { position: sticky; z-index: 4; }
.ppm-table thead tr.thead-row-months th { top: 0; }
.ppm-table thead tr.thead-row-days   th { top: 32px; box-shadow: 0 1px 4px rgba(0,0,0,.05); }

/* --- Sticky columns --- */
.sticky-col { position: sticky; left: 0; z-index: 3; background: #fff; isolation: isolate; }
.sticky-col--amount  { box-shadow: none; clip-path: none; }
.sticky-col--fact    { box-shadow: 4px 0 10px rgba(0,0,0,.05); clip-path: inset(0 -12px 0 0); }
.sticky-col:not(.sticky-col--amount):not(.sticky-col--fact) { box-shadow: none; clip-path: none; }
thead .sticky-col                       { z-index: 5; }
thead tr.thead-row-months .sticky-col  { z-index: 7; }
thead tr.thead-row-days   .sticky-col  { z-index: 7; }

/* ============================================================
   Header cells
   ============================================================ */
.th-node {
  background: #f8fafc;
  font-weight: 600; color: #475569;
  padding: 0 8px; text-align: center; vertical-align: middle;
  height: 32px; font-size: 10px; letter-spacing: .5px; text-transform: uppercase;
  position: relative; overflow: visible;
  border-bottom: 2px solid #e2e8f0;
}
.th-node__inner {
  display: flex; align-items: center; justify-content: center; gap: 5px;
}
.th-node__icon { color: #94a3b8; flex-shrink: 0; }
.th-amount {
  background: #f8fafc; font-weight: 600; font-size: 9px; letter-spacing: .5px; text-transform: uppercase;
  color: #64748b; text-align: center; padding: 0 4px;
  vertical-align: middle; line-height: 1.3; height: 32px;
  position: relative; overflow: visible;
  border-bottom: 2px solid #e2e8f0;
}
.col-resize-handle {
  position: absolute; top: 0; right: 0;
  width: 4px; height: 100%; cursor: col-resize; z-index: 10;
  background: transparent; transition: background 0.15s;
}
.col-resize-handle:hover,
.col-resize-handle:active { background: #3b82f6; opacity: 0.4; }

.th-month {
  text-align: center; background: #f8fafc; font-weight: 600;
  font-size: 10px; color: #64748b; padding: 0 4px;
  text-transform: capitalize; height: 32px;
  border-bottom: 2px solid #e2e8f0;
  vertical-align: middle; letter-spacing: .3px;
}
.th-month__label {
  position: sticky;
  display: inline-block;
}
.th-empty-period { color: #cbd5e1; font-weight: 400; }
.th-day {
  width: 38px; min-width: 36px; text-align: center;
  background: #f8fafc; padding: 3px 2px; vertical-align: middle; height: 30px;
}
.th-day--weekend { background: #fffbf2; }
.th-day--today {
  background: #eef4ff;
  position: relative;
}
.th-day--today::after {
  content: '';
  position: absolute; bottom: 0; left: 20%; right: 20%;
  height: 2px; background: #3b82f6; border-radius: 1px;
}
.day-num  { font-weight: 700; font-size: 11px; line-height: 1.2; color: #334155; }
.day-wd   { font-size: 8px;  color: #94a3b8; text-transform: lowercase; line-height: 1.1; letter-spacing: .3px; }
.day-unit { font-size: 7px;  color: #cbd5e1; line-height: 1; }

/* ============================================================
   Body rows
   Иерархия через левый бордер + вес шрифта, не через цвет фона
   ============================================================ */
.ppm-table tbody tr { height: 44px; }
.ppm-table tbody tr td { background: #fff; }
.ppm-table tbody tr td.sticky-col,
.ppm-table tbody tr td.sticky-col--amount,
.ppm-table tbody tr td.sticky-col--fact { background: #fff; }

.ppm-row--even td { background: #fafbfd; }
.ppm-row--even td.sticky-col,
.ppm-row--even td.sticky-col--amount,
.ppm-row--even td.sticky-col--fact { background: #fafbfd; }

.ppm-table tbody tr:hover td { background: #f7fbff !important; }
.ppm-table tbody tr:hover td.sticky-col,
.ppm-table tbody tr:hover td.sticky-col--amount,
.ppm-table tbody tr:hover td.sticky-col--fact { background: #f7fbff !important; }

/* Левый акцент по типу строки (только на sticky name column) */
.ppm-row--root   > .td-node { border-left: 4px solid #1e40af !important; background: #f0f4ff !important; }
.ppm-row--block  > .td-node { border-left: 4px solid #3b82f6 !important; }
.ppm-row--floor  > .td-node { border-left: 4px solid #7c3aed !important; }
.ppm-row--group  > .td-node { border-left: 4px solid #f59e0b !important; }
.ppm-row--work   > .td-node { border-left: 3px solid #e5e7eb !important; }

/* Root row — слегка выделен фон */
.ppm-row--root td                         { background: #f0f4ff !important; }
.ppm-row--root td.sticky-col,
.ppm-row--root td.sticky-col--amount,
.ppm-row--root td.sticky-col--fact        { background: #f0f4ff !important; }
.ppm-row--root:hover td                   { background: #dbeafe !important; }

/* Block row */
.ppm-row--block td                        { background: #f8fafc !important; }
.ppm-row--block td.sticky-col,
.ppm-row--block td.sticky-col--amount,
.ppm-row--block td.sticky-col--fact       { background: #f8fafc !important; }
.ppm-row--block:hover td                  { background: #f0f7ff !important; }

/* Floor row */
.ppm-row--floor td                        { background: #fbf8ff !important; }
.ppm-row--floor td.sticky-col,
.ppm-row--floor td.sticky-col--amount,
.ppm-row--floor td.sticky-col--fact       { background: #fbf8ff !important; }
.ppm-row--floor:hover td                  { background: #f3eeff !important; }

/* Placement row */

/* Group row */
.ppm-row--group td                        { background: #fffdf7 !important; }
.ppm-row--group td.sticky-col,
.ppm-row--group td.sticky-col--amount,
.ppm-row--group td.sticky-col--fact       { background: #fffdf7 !important; }
.ppm-row--group:hover td                  { background: #fef9ec !important; }

/* Несвязанный этаж */
.ppm-row--unlinked > .td-node             { border-left: 4px solid #f59e0b !important; }
.ppm-row--unlinked td                     { background: #fffbeb !important; }
.ppm-row--unlinked td.sticky-col,
.ppm-row--unlinked td.sticky-col--amount,
.ppm-row--unlinked td.sticky-col--fact    { background: #fffbeb !important; }
.ppm-row--unlinked:hover td               { background: #fef3c7 !important; }

/* --- Node cell --- */
.td-node { padding: 0; overflow: hidden; vertical-align: middle; }
.node-cell {
  display: flex; align-items: flex-start; gap: 4px;
  padding: 5px 8px 5px 4px;
}
.node-cell--floor { align-items: center; }

.expand-btn {
  background: none; border: none; cursor: pointer;
  padding: 2px 4px; border-radius: 4px;
  flex-shrink: 0; color: #9ca3af; line-height: 1;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.1s, color 0.1s;
}
.expand-btn:hover { background: #dbeafe; color: #1d4ed8; }
.expand-icon     { font-size: 14px; display: block; line-height: 1; }
.expand-spacer   { display: inline-block; width: 22px; flex-shrink: 0; }
.expand-spacer-spinner { width: 22px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; }

.node-icon--floor { color: #7c3aed; margin-right: 1px; flex-shrink: 0; }
.node-icon--group { color: #d97706; margin-right: 1px; flex-shrink: 0; }
.node-icon--work  { color: #94a3b8; margin-right: 1px; flex-shrink: 0; }
.node-icon--work-no-usp { color: #ef4444 !important; }

.unlinked-badge {
  display: inline-flex; align-items: center; gap: 3px;
  margin-left: 6px; padding: 1px 7px;
  background: #fef3c7; color: #92400e; border: 1px solid #fcd34d;
  font-size: 10px; font-weight: 600; border-radius: 10px;
  white-space: nowrap; flex-shrink: 0;
}

.ppm-row--no-works td { background: #f3f4f6 !important; }
.ppm-row--no-works td.sticky-col,
.ppm-row--no-works td.sticky-col--amount,
.ppm-row--no-works td.sticky-col--fact { background: #f3f4f6 !important; }
.ppm-row--no-works > .td-node { border-left: 4px solid #d1d5db !important; }
.ppm-row--no-works .node-label { color: #9ca3af !important; }
.ppm-row--no-works:hover td { background: #e5e7eb !important; }

.no-works-badge {
  display: inline-flex; align-items: center; gap: 3px;
  margin-left: 6px; padding: 1px 7px;
  background: #f3f4f6; color: #6b7280; border: 1px solid #d1d5db;
  font-size: 10px; font-weight: 600; border-radius: 10px;
  white-space: nowrap; flex-shrink: 0;
}

.node-warning-icon {
  color: #f59e0b;
  flex-shrink: 0;
}

.no-usp-work-badge {
  display: inline-flex; align-items: center; gap: 3px;
  margin-left: 6px; padding: 1px 7px;
  background: #fff7ed; color: #c2410c; border: 1px solid #fdba74;
  font-size: 10px; font-weight: 600; border-radius: 10px;
  white-space: nowrap; flex-shrink: 0;
}
.smeta-blocked-badge {
  display: inline-flex; align-items: center; gap: 3px;
  margin-left: 6px; padding: 1px 7px;
  background: #fef2f2; color: #b91c1c; border: 1px solid #fca5a5;
  font-size: 10px; font-weight: 600; border-radius: 10px;
  white-space: nowrap; flex-shrink: 0;
}

.work-select-cb { flex-shrink: 0; margin-right: 2px; }

.complete-row-btn {
  flex-shrink: 0; margin-left: 2px;
  opacity: 0; transition: opacity 0.15s;
}
.ppm-row--work:hover .complete-row-btn { opacity: 1; }
.complete-row-btn--done { opacity: 1 !important; }

/* ============================================================
   Node labels
   ============================================================ */
.node-label {
  font-size: 13px; color: #374151;
  white-space: normal; word-break: break-word; line-height: 1.4;
}
.node-label--root  { font-weight: 700; color: #1e3a8a; font-size: 13px; }
.node-label--block { font-weight: 600; color: #1e40af; font-size: 13px; }
.node-label--floor { font-weight: 600; color: #5b21b6; font-size: 12px; }
.node-label--group { font-weight: 600; color: #92400e; font-size: 12px; }
.node-label--work  { font-weight: 400; color: #374151; font-size: 12px; }

.cell-amount-val {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 4px 4px;
}
.amount-plan-val {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
}
.amount-unit {
  display: inline-block;
  padding: 1px 7px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 500;
  border-radius: 99px;
  line-height: 1.6;
  white-space: nowrap;
  letter-spacing: .1px;
}
.cell-fact-val {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 5px 4px;
}
.fact-sum {
  font-size: 14px;
  font-weight: 700;
  color: #d1d5db;
  line-height: 1.2;
}
.fact-sum--has  { color: #1d4ed8; }
.fact-sum--over { color: #dc2626; }
.fact-pct {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  line-height: 1.2;
  background: #f1f5f9;
  padding: 0 5px;
  border-radius: 99px;
}

.cell-hierarchy-pct {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}
.cell-group-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}
.hierarchy-fact-pct {
  font-size: 13px;
  font-weight: 700;
  color: #1e40af;
  background: #eff6ff;
  padding: 1px 8px;
  border-radius: 99px;
  line-height: 1.5;
  white-space: nowrap;
}
.hierarchy-fact-pct--full {
  color: #166534;
  background: #dcfce7;
}

/* --- Fixed cols --- */
.td-amount, .td-fact { vertical-align: middle; padding: 0; overflow: hidden; }

/* --- Day cells --- */
.td-day { width: 38px; min-width: 36px; vertical-align: middle; padding: 0; }
.td-day--weekend { background: #fefce8 !important; }
.td-day--today {
  background: linear-gradient(180deg, rgba(219, 234, 254, 0.55) 0%, rgba(239, 246, 255, 0.72) 100%) !important;
}
.td-empty { background: #f9fafb; }

/* --- Cell inputs --- */
.cell-input {
  display: block; width: 100%; min-height: 26px;
  padding: 2px 4px; border: none; background: transparent;
  outline: none; font-size: 12px; text-align: center; color: #222;
  box-sizing: border-box;
}
.cell-input:hover { background: #ecf4ff; }
.cell-input:focus { background: #deeeff; box-shadow: inset 0 0 0 1.5px #2979d8; outline: none; }
.cell-input--num { -moz-appearance: textfield; }
.cell-input--num::-webkit-inner-spin-button,
.cell-input--num::-webkit-outer-spin-button { -webkit-appearance: none; }

/* Read-only cells */
.cell-ro {
  min-height: 26px; display: flex; align-items: center;
  justify-content: center; color: #9aa0a6; font-size: 12px; user-select: none;
}
/* Подпись периодов под именем этажа */
.node-label-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.node-label-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
}
.node-label-wrap--expandable {
  cursor: pointer;
  user-select: none;
}
.floor-gpr-sub--loading {
  display: flex;
  align-items: center;
  height: 14px;
}
.floor-gpr-rows {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.floor-gpr-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  line-height: 1.4;
  white-space: nowrap;
}
.floor-gpr-label {
  font-size: 9.5px;
  font-weight: 600;
  min-width: 26px;
  opacity: .6;
}
.floor-gpr-row--base .floor-gpr-label { color: #2e7d32; }
.floor-gpr-row--cur  .floor-gpr-label { color: #1565c0; }
.floor-gpr-row--base .floor-gpr-dates { color: #33691e; }
.floor-gpr-row--cur  .floor-gpr-dates { color: #1a237e; }
.cell-ro--empty   { color: transparent; }

.cell-ro--parent {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  gap: 2px;
  min-height: 38px;
}
.cell-parent__smr {
  font-size: 11.5px;
  font-weight: 700;
  color: #1e40af;
  white-space: nowrap;
  line-height: 1.3;
  cursor: default;
}
.cell-parent__tmc {
  font-size: 10.5px;
  font-weight: 600;
  color: #7c3aed;
  white-space: nowrap;
  line-height: 1.3;
  cursor: default;
}
.cell-parent__label {
  font-size: 9px;
  font-weight: 700;
  opacity: 0.55;
  letter-spacing: 0.3px;
  margin-right: 2px;
}
.cell-work__smr {
  font-size: 10px;
  font-weight: 600;
  color: #1e40af;
  white-space: nowrap;
  line-height: 1.2;
}
.cell-work__tmc {
  font-size: 10px;
  font-weight: 600;
  color: #7c3aed;
  white-space: nowrap;
  line-height: 1.2;
}

.ppm-table td.td-ro { background: #f5f6f8; border-color: #e8e8e8; }
.ppm-table td.td-ro.td-day--weekend { background: #f9f4e8; }
.ppm-table td.td-ro .cell-ro { opacity: 0.75; }
.ppm-table td.td-ro:hover { background: inherit !important; }

/* ============================================================
   Misc
   ============================================================ */
.project-list { max-height: 520px; overflow: auto; }
.json-pre {
  background: #f5f5f5; padding: 16px; border-radius: 8px;
  overflow: auto; max-height: calc(100vh - 140px); font-size: 12px;
}
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 11px; }

/* ============================================================
   USP Dialog
   ============================================================ */
.usp-dialog {
  display: flex;
  flex-direction: column;
}
.usp-dialog__header {
  flex-shrink: 0;
}
.usp-dialog__body {
  flex: 1;
  overflow: hidden;
  padding: 12px 16px;
}
.usp-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 40px;
  justify-content: center;
  color: #666;
  font-size: 14px;
}
.usp-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 60px 0;
  color: #aaa;
  font-size: 15px;
}
.usp-empty-hint {
  font-size: 12px;
  color: #bbb;
}
/* Двухколоночный layout */
.usp-layout {
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 16px;
  height: calc(100vh - 130px);
}
/* Левая колонка — список работ */
.usp-works-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
}
.usp-works-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 6px;
}
.usp-works-header span { flex: 1; }
.usp-works-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}
.usp-work-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}
.usp-work-row:last-child { border-bottom: none; }
.usp-work-row:hover { background: #f5f7fa; }
.usp-work-row--sent {
  background: #f0fff4;
}
.usp-work-row--multi-date {
  background: #fff8f0;
}
.usp-work-check {
  flex-shrink: 0;
  margin-top: 2px;
}
.usp-work-info {
  flex: 1;
  min-width: 0;
}
.usp-work-name {
  font-size: 13px;
  color: #2c3e50;
  line-height: 1.4;
}
.usp-work-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
}
.usp-work-sum {
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  background: #eceff1;
  border-radius: 4px;
  padding: 1px 6px;
}
.usp-work-floors {
  font-weight: 400;
  color: #78909c;
}
.usp-work-guid {
  font-size: 10px;
  color: #9e9e9e;
  font-family: ui-monospace, monospace;
}
/* Правая колонка — JSON */
.usp-json-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
}
.usp-json-panel--empty {
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px dashed #e0e0e0;
}
.usp-json-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  padding: 6px 10px;
  background: #2d2d3f;
  color: #cdd6f4;
  border-radius: 8px 8px 0 0;
}
.usp-pre {
  background: #1e1e2e;
  color: #cdd6f4;
  padding: 12px 16px;
  flex: 1;
  overflow: auto;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  white-space: pre;
  line-height: 1.6;
  border-radius: 0 0 8px 8px;
  margin: 0;
}
/* ============================================================
   Cell click (вместо input)
   ============================================================ */
.cell-click {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 34px;
  cursor: pointer;
  border-radius: 5px;
  transition: background 0.1s, box-shadow 0.1s, transform 0.08s;
  padding: 4px 3px;
  gap: 2px;
}
/* Пустая — мягкий плюс, при наведении намекает что можно ввести */
.cell-click:hover {
  background: #eff6ff;
  box-shadow: inset 0 0 0 1px #bfdbfe;
}
.cell-click__val {
  font-size: 13px;
  font-weight: 600;
  color: #1e40af;
  line-height: 1.2;
}
.cell-click__empty {
  font-size: 18px;
  color: #e2e8f0;
  line-height: 1;
  font-weight: 300;
  transition: color 0.1s;
}
.cell-click:hover .cell-click__empty {
  color: #93c5fd;
}

/* Заполнено (введено, не сохранено) */
.cell-click--filled {
  background: #eff6ff;
}
.cell-click--filled:hover {
  background: #dbeafe;
  box-shadow: inset 0 0 0 1px #93c5fd;
}

/* Сохранено, не отправлено */
.cell-click--saved {
  background: #f8fafc;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}
.cell-click--saved .cell-click__val { color: #475569; }
.cell-click--saved:hover { background: #f1f5f9; }

/* Отправлено в УСП */
.cell-click--sent {
  background: #f0fdf4;
  box-shadow: inset 0 0 0 1px #bbf7d0;
}
.cell-click--sent .cell-click__val { color: #15803d; }
.cell-click--sent:hover { background: #dcfce7; }

/* Перерасход */
.cell-click--over {
  background: #fff1f2;
  box-shadow: inset 0 0 0 1.5px #fca5a5;
}
.cell-click--over .cell-click__val { color: #b91c1c; }
.cell-click--over:hover { background: #ffe4e6; }

/* Завершено — заблокировано */
.cell-click--done {
  background: transparent;
  cursor: default;
  pointer-events: none;
  opacity: 0.45;
}
.cell-click--done .cell-click__val { color: #64748b; }
.cell-click--smeta-blocked {
  background: repeating-linear-gradient(
    135deg, #fef2f2, #fef2f2 4px, #fecaca 4px, #fecaca 6px
  );
  cursor: not-allowed;
  opacity: 0.7;
}
.cell-click--smeta-blocked .cell-click__val { color: #991b1b; }
.cell-click--future {
  pointer-events: none;
  opacity: 0.3;
  background: repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,.04) 3px, rgba(0,0,0,.04) 5px);
}

/* Шильдик УСП — в правом верхнем углу ячейки */
.cell-click__badge {
  position: absolute;
  right: 1px;
  top: 1px;
  font-size: 7px;
  font-weight: 700;
  color: #15803d;
  background: rgba(220, 252, 231, 0.85);
  border-radius: 3px 0 4px 0;
  padding: 0 3px;
  letter-spacing: 0.5px;
  line-height: 1.6;
  text-transform: uppercase;
  pointer-events: none;
  border: 1px solid;
}
.cell-click__badge--no-usp {
  color: #dc2626;
  background: rgba(254, 226, 226, 0.85);
  pointer-events: auto;
  cursor: default;
}
.cell-click__badge--correction {
  color: #fff;
  background: #ff6d00;
  border-color: #e65100;
}
.cell-click--pending-correction {
  border: 2px solid #ff6d00 !important;
}

/* ============================================================
   Диалог: Заполнение Оперфакта
   ============================================================ */
.fact-dialog {
  width: 620px;
  max-width: 98vw;
  border-radius: 16px !important;
  overflow: hidden;
}
.fact-dialog__header {
  background: #44169f;
  color: #fff;
  padding: 14px 48px 14px 18px;
  position: relative;
  flex-shrink: 0;
}
.fact-dialog__header--sent {
  background: linear-gradient(135deg, #388e3c 0%, #1b5e20 100%);
}
.fact-sent-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  color: #2e7d32;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 4px;
}
/* Стили режима сторно вынесены в ./styles/storno.css */
.fact-dialog__title {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
}
.fact-dialog__subtitle {
  font-size: 12px;
  opacity: .95;
  margin-top: 1px;
  line-height: 1.2;
}
.fact-dialog__close {
  position: absolute !important;
  top: 8px;
  right: 8px;
}
.fact-dialog__body {
  padding: 14px 18px 16px;
  background: #fff;
}
.fact-dialog__body > * {
  margin-bottom: 14px;
}
.fact-dialog__body > *:last-child {
  margin-bottom: 0;
}
.fact-dialog__loader {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.fact-dialog__footer {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px 16px;
  background: #fff;
  border-top: 0;
  flex-shrink: 0;
  justify-content: flex-end;
}
.fact-dialog__save-btn {
  border-radius: 12px !important;
  padding-left: 18px !important;
  padding-right: 18px !important;
}

/* Карточка работы */
.fwork-card {
  background: #eef4fa;
  border-radius: 18px;
  border: 0;
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.fwork-card__name-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.fwork-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #333;
  line-height: 1.25;
  flex: 1;
}
.fwork-card__action-btn {
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
  border-radius: 8px !important;
  background: #d8e0eb;
}
.fwork-card__vol-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.fwork-vol__text {
  font-size: 14px;
  color: #546273;
  font-weight: 600;
}
.fwork-vol__done {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #43a047;
}
.fwork-vol__badge-outline {
  font-size: 12px;
  font-weight: 600;
  color: #6d7b8a;
  border: 1px solid #c7d1dc;
  border-radius: 999px;
  padding: 4px 12px;
}
.fwork-executor {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.fwork-executor__label {
  font-size: 13px;
  color: #546273;
  font-weight: 600;
}
.fwork-executor__chip {
  font-size: 12px !important;
  font-weight: 700;
  border-radius: 4px !important;
  padding: 2px 8px !important;
  background: #1f62d8 !important;
}
.fwork-executor__val {
  font-size: 13px;
  color: #333;
  font-weight: 600;
}

/* Ввод факта + полоса */
.fact-input-section {
  background: #eef4fa;
  border-radius: 18px;
  border: 0;
  padding: 18px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.fact-input-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.fact-input-row__left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.fact-input-row__label {
  font-size: 14px;
  font-weight: 700;
  color: #465364;
  white-space: nowrap;
}
.fact-input-row__field {
  width: 130px;
}
.fact-input-row__unit {
  font-size: 13px;
  color: #465364;
  font-weight: 600;
  white-space: nowrap;
}
.fact-cumulative-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #546e7a;
  margin-bottom: 4px;
}
.fact-cumulative-row__label { font-weight: 400; }
.fact-cumulative-row__val   { color: #37474f; font-weight: 700; }

.fact-input-row__right {
  font-size: 13px;
  color: #465364;
  font-weight: 600;
}
.fact-input-row__remainder {
  color: #225ad8;
  font-weight: 700;
  margin-left: 2px;
}
.fact-input-row__field .q-field__control,
.fact-mat-item__input .q-field__control {
  border-radius: 10px !important;
  background: #fff;
}
.fact-input-row__field .q-field__native,
.fact-mat-item__input .q-field__native {
  font-size: 14px;
}

/* Полоса факта — Opera Build */
.fwork-bar {
  width: 100%;
  height: 24px;
  background: #e6e8ef;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  user-select: none;
}
.fwork-bar .fwork-bar__fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 6px;
  background: #1faa4a;
  transition: width .15s ease;
  min-width: 0;
  overflow: hidden;
}
.fwork-bar .fwork-bar__lock {
  position: absolute;
  top: 50%;
  transform: translate(-100%, -50%);
  line-height: 1;
  z-index: 3;
  pointer-events: none;
}
.fwork-bar .fwork-bar__pct-pill {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #0f6a2f;
  border: 2px solid #fff;
  border-radius: 8px;
  padding: 1px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  z-index: 2;
}

/* Материалы */
.fact-materials {
  background: #eef4fa;
  border-radius: 18px;
  border: 0;
  overflow: hidden;
}
.fact-materials__header {
  display: flex;
  align-items: center;
  padding: 18px 18px 8px;
  gap: 4px;
}
.fact-materials__title {
  font-size: 13px;
  font-weight: 700;
  color: #333;
}
.fact-materials__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 20px;
  padding: 6px 18px 18px;
  border-top: 0;
}
.fact-mat-item {
  transition: background 0.15s;
  border-radius: 10px;
  padding: 0;
}
.fact-mat-item--manual {
  background: transparent;
  border: 0;
}
.fact-mat-item--replaced {
  background: transparent;
  border: 0;
}
.fact-mat-item__name-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}
.fact-mat-item__name {
  font-size: 12px;
  color: #333;
  line-height: 1.35;
  flex: 1;
}
.fact-mat-item__badge {
  flex-shrink: 0;
  font-size: 9px;
}
.fact-mat-item__input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.fact-mat-item__input {
  flex: 1;
}
.fact-mat-item__info {
  font-size: 11px;
  color: #5f6c7d;
}
.fact-mat-item__formula {
  color: #5f6c7d;
  margin-left: 4px;
}
.fact-mat-item__swap-btn {
  width: 22px !important;
  height: 22px !important;
  min-width: 22px !important;
  flex-shrink: 0;
}
/* ============================================================
   Модалка: Выбор материала (пересорт)
   ============================================================ */
.mat-picker {
  display: flex;
  flex-direction: column;
  width: 680px;
  max-width: 96vw;
  height: 75vh;
  max-height: 75vh;
  border-radius: 12px !important;
  overflow: hidden;
}
.mat-picker__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: linear-gradient(135deg, #3949ab 0%, #1a237e 100%);
  color: #fff;
  padding: 12px 20px;
  flex-shrink: 0;
}
.mat-picker__title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mat-picker__title > div:first-child {
  font-size: 15px;
  font-weight: 700;
}
.mat-picker__subtitle {
  font-size: 11px;
  opacity: .75;
  max-width: 700px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mat-picker__search-bar {
  padding: 8px 16px 6px;
  background: #f4f6fb;
  border-bottom: 1px solid #e0e4ef;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mat-picker__search-row {
  display: flex;
  align-items: center;
}
.mat-picker__search-input {
  width: 380px;
  max-width: 100%;
}
.mat-picker__hint {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 11px;
  color: #7986cb;
  background: #eef0fb;
  border-radius: 6px;
  padding: 5px 10px;
  line-height: 1.5;
}
.mat-picker__body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px 16px;
}
.mat-picker__count {
  font-size: 11px;
  color: #90a4ae;
  padding: 6px 2px 4px;
}
.mat-picker__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 60px 0;
  color: #aaa;
  font-size: 14px;
}
.mat-picker__list {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.mat-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}
.mat-picker__item:hover {
  background: #eef1fb;
  border-color: #c5cae9;
}
.mat-picker__item-left {
  flex: 1;
  min-width: 0;
}
.mat-picker__item-name {
  font-size: 13px;
  color: #1a237e;
  font-weight: 500;
  line-height: 1.4;
}
.mat-picker__item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}
.mat-picker__item-type {
  font-size: 10px;
  color: #fff;
  background: #7986cb;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}
.mat-picker__item-code {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10.5px;
  color: #78909c;
  font-family: ui-monospace, monospace;
}
.mat-picker__item-guid {
  font-size: 10px;
  color: #b0bec5;
  font-family: ui-monospace, monospace;
  letter-spacing: -.3px;
}
.mat-picker__item-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.mat-picker__item-unit {
  font-size: 11px;
  color: #546e7a;
  background: #eceff1;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

.fact-materials__empty {
  padding: 14px;
  font-size: 12px;
  color: #aaa;
  text-align: center;
  border-top: 1px solid #eee;
}

/* ═══════════════════════════════════════════════════
   Ячейки конструктива (группы) в матрице
   ═══════════════════════════════════════════════════ */
.cell-click--group {
  cursor: pointer;
  background: #fef9ec;
  border-radius: 5px;
  min-height: 34px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 3px;
  gap: 2px;
  transition: background 0.1s, box-shadow 0.1s;
  box-shadow: inset 0 0 0 1px #fde68a;
}
.cell-click--group:hover {
  background: #fef3c7;
  box-shadow: inset 0 0 0 1px #fbbf24;
}
.ppm-table--kzt .cell-click--group {
  min-height: 44px;
}
.cell-click__val--group {
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  line-height: 1.2;
}
/* KZT-сумма под объёмом в ячейке дня */
.cell-click__kzt {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  border-top: 1px solid #f1f5f9;
  padding-top: 2px;
  margin-top: 1px;
  min-width: 100%;
  text-align: center;
}
.cell-click__kzt::after {
  content: ' ₸';
  font-size: 9px;
  color: #94a3b8;
}
.cell-click__kzt--na {
  color: #cbd5e1;
}
.cell-click__kzt--na::after { content: ''; }
/* KZT-кнопка в тулбаре */
.kzt-toggle-btn {
  font-weight: 700;
  letter-spacing: 0.5px;
}
/* KZT-блок в диалоге факта */
.fact-kzt-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 6px;
  padding: 6px 10px;
  background: #e0f2f1;
  border-radius: 8px;
  border: 1px solid #b2dfdb;
}
.fact-kzt-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.fact-kzt-row--mats .fact-kzt-row__label,
.fact-kzt-row--mats .fact-kzt-row__amount {
  color: #1565c0;
}
.fact-kzt-row--total {
  border-top: 1px solid #b2dfdb;
  padding-top: 3px;
  margin-top: 2px;
}
.fact-kzt-row__label {
  font-size: 12px;
  color: #00695c;
  font-weight: 500;
  min-width: 80px;
}
.fact-kzt-row__amount {
  font-size: 13px;
  font-weight: 700;
  color: #00695c;
  text-align: right;
}
.fact-kzt-row__amount--total {
  font-size: 14px;
  color: #004d40;
}
/* Значение в заблокированной ячейке конструктива */
.cell-group-done__val {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}
.cell-click--done-group {
  min-height: 34px;
  border-radius: 5px;
  justify-content: center;
  opacity: 0.8;
  pointer-events: auto;
  cursor: pointer;
}
.cell-click--done-group .cell-group-done__val {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}
.cell-group-status {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.cell-click__empty--group {
  font-size: 18px;
  color: #fde68a;
  font-weight: 300;
  transition: color 0.1s;
}
.cell-click--group:hover .cell-click__empty--group {
  color: #f59e0b;
}

/* ═══════════════════════════════════════════════════
   Диалог: Факт по конструктиву
   ═══════════════════════════════════════════════════ */
.gfd-card {
  width: 560px;
  max-width: 95vw;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.gfd-header {
  background: linear-gradient(135deg, #5c35b1 0%, #7e57c2 100%);
}
.gfd-body {
  flex: 1;
  overflow-y: auto;
  max-height: 65vh;
  padding: 12px 16px 4px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Процентный ползунок группы */
.gfd-pct-section {
  background: #ede7f6;
  border: 1px solid #ce93d8;
  border-radius: 10px;
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.gfd-pct-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.gfd-pct-label {
  font-size: 12px;
  color: #4527a0;
  font-weight: 500;
}
.gfd-group-bar {
  background: #d1c4e9;
}
.gfd-group-bar .fwork-bar__fill {
  background: #7e57c2;
}
.gfd-group-bar .fwork-bar__pct-pill {
  background: #4527a0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.gfd-pill-lock {
  opacity: 0.85;
  margin-right: 1px;
}

/* Пустое состояние */
.gfd-empty {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px;
  justify-content: center;
  color: #bbb;
  font-size: 13px;
}

/* Список работ */
.gfd-works-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.gfd-work-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
  transition: border-color 0.1s;
}
.gfd-work-item--sent {
  background: #f1f8e9;
  border-color: #a5d6a7;
}
.gfd-work-item--complete {
  background: #f1f8e9;
  border-color: #66bb6a;
  opacity: 0.85;
}
.gfd-work-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #263238;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.gfd-work-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.gfd-work-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  color: #607d8b;
  flex: 1;
  min-width: 0;
}
.gfd-work-plan  { color: #37474f; }
.gfd-work-cum   { color: #7b5ea7; }
.gfd-work-rem   { color: #00796b; }
.gfd-work-input-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.gfd-work-input {
  width: 110px;
  min-width: 110px;
}
.gfd-work-unit {
  font-size: 11px;
  color: #78909c;
  white-space: nowrap;
}

/* Прогресс-бар работы */
.gfd-work-bar {
  position: relative;
  height: 6px;
  background: #e8eaf6;
  border-radius: 4px;
  margin-top: 8px;
  overflow: hidden;
}
.gfd-work-bar__fill {
  height: 100%;
  background: #7e57c2;
  border-radius: 3px;
  transition: width 0.2s;
}
.gfd-work-bar__fill--over {
  background: #e53935;
}
.gfd-work-bar__pct {
  position: absolute;
  right: 0;
  top: -14px;
  font-size: 10px;
  color: #7e57c2;
  font-weight: 600;
}

/* Материалы в групповом диалоге */
.gfd-mats {
  margin-top: 8px;
  padding: 8px 10px;
  background: #f9f7ff;
  border: 1px solid #e0d8f8;
  border-radius: 6px;
}
.gfd-mats__title {
  font-size: 11px;
  font-weight: 700;
  color: #6b3faa;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.gfd-mat-item {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ede8fb;
}
.gfd-mat-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
.gfd-mat-item--manual .gfd-mat-name {
  color: #e65100;
}
.gfd-mat-name {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #333;
  margin-bottom: 4px;
  line-height: 1.35;
}
.gfd-mat-badge {
  flex-shrink: 0;
  margin-top: 1px;
}
.gfd-mat-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.gfd-mat-input {
  width: 100px;
  flex-shrink: 0;
}
.gfd-mat-unit {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}
.gfd-mat-info {
  margin-top: 3px;
  font-size: 10px;
  color: #888;
}

.usp-report-card {
  display: flex;
  flex-direction: column;
}
.usp-report-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  font-size: 11px;
  white-space: nowrap;
}
.usp-report-table th.usp-th {
  cursor: pointer;
  user-select: none;
  transition: background .15s;
}
.usp-report-table th.usp-th:hover {
  background: #e0e0e0;
}
.usp-filter-input {
  font-size: 12px;
}
.usp-filter-input .q-field__control {
  min-height: 28px !important;
  height: 28px !important;
}
.usp-filter-input .q-field__native,
.usp-filter-input .q-field__append,
.usp-filter-input .q-field__prepend {
  min-height: 28px !important;
  padding: 0 4px;
  font-size: 12px;
}
.usp-report-table td {
  font-size: 12px;
  padding: 4px 8px;
}
.usp-report-footer {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 16px;
  background: #eceff1;
  border-top: 2px solid #b0bec5;
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
  flex-shrink: 0;
}
.usp-footer-item {
  white-space: nowrap;
}
.usp-report-table tbody tr:hover {
  background: #e3f2fd !important;
}
.usp-row--sent {
  background: #f0fdf4;
}
.usp-row--not-sent {
  background: #fefce8;
}
.mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
}

/* --- Fact History Dialog --- */
.cell-fact-clickable {
  cursor: pointer;
}
.cell-fact-clickable:hover {
  background: #eff6ff !important;
}
.fact-history-card {
  display: flex;
  flex-direction: column;
  max-height: 100vh;
}
.fact-history-summary {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}
.fact-history-chip {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 99px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 13px;
  font-weight: 500;
}
.fact-history-chip--count {
  background: #f1f5f9;
  color: #64748b;
}
.fact-history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}
.fact-history-table th {
  position: sticky;
  top: 38px;
  z-index: 2;
  background: #f8fafc;
  padding: 8px 10px;
  font-weight: 600;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  text-align: left;
  white-space: nowrap;
}
.fact-history-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fact-history-table tbody tr:hover {
  background: #f8fafc;
}
.fh-col-date { width: 90px; }
.fh-col-block { width: 80px; }
.fh-col-floor { width: 70px; }
.fh-col-group { width: 14%; }
.fh-col-work  { width: auto; }
.fh-col-vol   { width: 80px; }
.fh-col-smr   { width: 120px; }
.fh-col-tmc   { width: 120px; }
.fh-col-guid  { width: 280px; }
.fh-col-sent  { width: 50px; }
.fh-cell-guid {
  font-size: 11px;
  color: #64748b;
  font-family: monospace;
  white-space: nowrap;
  overflow: visible;
  text-overflow: unset;
  display: flex;
  align-items: center;
  gap: 2px;
}
.fh-guid-text {
  user-select: all;
}
.fh-guid-copy {
  opacity: 0.4;
  transition: opacity 0.15s;
}
.fh-cell-guid:hover .fh-guid-copy {
  opacity: 1;
}
.fh-filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: flex-end;
}
.fh-group-block {
  margin-bottom: 18px;
}
.fh-group-header {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #e0f2fe;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
}
.fh-group-title {
  font-weight: 700;
  font-size: 14px;
  color: #0c4a6e;
}
.fh-group-totals {
  font-size: 12px;
  color: #475569;
}

/* ── Discrepancy report ── */
.disc-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.disc-filters {
  flex-shrink: 0;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}
.disc-table {
  width: 100%;
}
.disc-table th,
.disc-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  padding: 3px 6px;
}
.disc-table thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 3;
}
.disc-table thead tr:nth-child(2) th {
  position: sticky;
  top: 28px;
  z-index: 3;
}
.disc-th-info {
  background: #eceff1;
  color: #37474f;
  font-weight: 600;
  border-bottom: 2px solid #b0bec5;
}
.disc-th-ours {
  background: #e3f2fd;
  color: #0d47a1;
  font-weight: 600;
  border-bottom: 2px solid #64b5f6;
}
.disc-th-usp {
  background: #f3e5f5;
  color: #4a148c;
  font-weight: 600;
  border-bottom: 2px solid #ba68c8;
}
.disc-th-delta {
  background: #fff3e0;
  color: #e65100;
  font-weight: 600;
  border-bottom: 2px solid #ffb74d;
}
.disc-cell-work {
  white-space: normal !important;
  word-break: break-word;
  line-height: 1.3;
  max-width: 180px;
}
.disc-cell-guid {
  white-space: nowrap;
  position: relative;
}
.disc-guid-text {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
  color: #546e7a;
}
.disc-guid-copy {
  opacity: 0.3;
  transition: opacity 0.15s;
}
.disc-table tbody tr:hover .disc-guid-copy {
  opacity: 1;
}
.disc-cell-ours { background: #f5f9ff; }
.disc-cell-usp  { background: #faf5fc; }
.disc-table tbody tr:hover td {
  filter: brightness(0.96);
}
.disc-row-ok { }
.disc-row-diff {
  background: #fff8e1;
}
.disc-row-only-ours {
  background: #e3f2fd;
}
.disc-row-only-usp {
  background: #f3e5f5;
}
.disc-footer {
  flex-shrink: 0;
  border-top: 2px solid #bf360c;
  background: #fafafa;
  padding: 6px 12px;
}
.disc-footer-row {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 12px;
  flex-wrap: wrap;
}
.disc-footer-label {
  font-weight: 700;
  font-size: 13px;
  color: #37474f;
  white-space: nowrap;
}
.disc-footer-group {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
}
.disc-footer-title {
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.disc-footer-ours {
  background: #e3f2fd;
  color: #0d47a1;
}
.disc-footer-usp {
  background: #f3e5f5;
  color: #4a148c;
}
.disc-footer-delta {
  background: #fff3e0;
  color: #e65100;
}

/* Detail dialog */
.disc-detail-table th,
.disc-detail-table td {
  font-size: 12px;
  padding: 4px 8px;
  white-space: nowrap;
}
.disc-detail-diff {
  background: #fff8e1;
}
.disc-detail-total {
  background: #eceff1;
  border-top: 2px solid #90a4ae;
}
</style>
